import { ArtifactPickerOption } from '../hyperscene-advanced/components/artifact-picker/artifact-picker.component';
import { formatProcessingDuration, getProcessingDurationMs } from '../hyperscene-advanced/services/processing-duration';
import { VIEWER_VISUAL_QUALITY_PROFILES } from '../hyperscene-advanced/models/viewer-visual-quality';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { getUuid } from '@sibe/utilities/common';
import { HypersceneIngestState } from '../hyperscene-advanced/models/hyperscene-ingest-state.interface';
import { isAnnotationNode } from '../hyperscene-advanced/services/viewer-annotations.store';
import {
  HypersceneDocumentView,
  HypersceneIngestAdapterService,
  HypersceneModelView,
  HypersceneModelSceneParticipationView,
  HypersceneSimilarityRunView,
  HypersceneNodeSmallView,
  HypersceneNodeView,
  HypersceneProcessingView,
  HypersceneSceneModelInstanceView,
  HypersceneSceneSettings,
  HypersceneSceneView,
  HypersceneTransformVectorView,
} from '../hyperscene-advanced/services/hyperscene-ingest-adapter.service';
import { HS_GEOMETRY_DECODE_CANCELED } from '@sibe/hyperscene/sources/scene';
import {
  type HsPhotogrammetryProject,
  type HsPhotogrammetryProjectFile,
  type HsRvmAttPair,
  type HsRvmAttProject,
  hsSelectPhotogrammetryProject,
  hsValidateRvmAttProject,
} from '@sibe/hyperscene/api/ingest';
import { HsSceneTree, hsMakeSceneTreeNode, type HsSceneTreeNode } from '@sibe/hyperscene/api/viewer';
const PROCESSING_POLL_INTERVAL_MS: number = 1500;
const PROCESSING_POLL_ATTEMPTS: number = 120;
const SIMILARITY_POLL_INTERVAL_MS: number = 5000;
interface SimilarityRerunTask {
  modelId: string;
  status: string;
  processingId?: string;
  error?: string;
}

interface SceneGeometryReuseResponse {
  processingIds: string[];
}

interface ArtifactsConfirmation {
  title: string;
  message: string;
  confirmLabel: string;
  run: () => Promise<void>;
}

const ARTIFACTS_PROCESSINGS_PAGE_SIZE: number = 20;
const DECODE_CANCELED_ERROR_MESSAGE: string = HS_GEOMETRY_DECODE_CANCELED;

interface HypersceneSceneModelInstanceDraft {
  modelId: string | null;
  instanceName: string;
  translation: HypersceneTransformVectorView;
  rotation: HypersceneTransformVectorView;
  scale: HypersceneTransformVectorView;
}

interface HypersceneSceneTransformDraft {
  translation: HypersceneTransformVectorView;
  rotation: HypersceneTransformVectorView;
  scale: HypersceneTransformVectorView;
}

type HypersceneSceneTreeNode = HsSceneTreeNode;

interface HypersceneRuntimeConfig {
  apiBaseUrl?: string;
}

@Component({
  selector: 'hyperscene-artifacts',
  templateUrl: './hyperscene-artifacts.component.html',
  styleUrls: [
    '../hyperscene-advanced/hyperscene-advanced.component.scss',
    '../hyperscene-advanced/artifacts-workspace.scss',
  ],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
/* eslint-disable @typescript-eslint/member-ordering */
export class HypersceneArtifactsDemo implements AfterViewInit, OnDestroy {
  public readonly visualQualityProfiles: typeof VIEWER_VISUAL_QUALITY_PROFILES = VIEWER_VISUAL_QUALITY_PROFILES;
  public ingestApiBaseUrl: string = this.resolveDefaultIngestApiBaseUrl();
  public ingestParserType: string = 'ifc';
  public ingestTag: string = '';
  public registryUseTagFilter: boolean = false;
  public modelsRegistry: HypersceneModelView[] = [];
  public modelSearchQuery: string = '';
  public selectedModelId: string | null = null;
  public selectedModelScenes: HypersceneModelSceneParticipationView[] = [];
  public selectedModelScenesLoading: boolean = false;
  public selectedModelScenesError: string | null = null;
  private selectedModelScenesRequestId: number = 0;
  // A full rerun restores previous replacements first, so stale matches are replaced without a reparse.
  public similarityFullRerun: boolean = true;
  public similarityBulkModelIds: string[] = [];
  public similarityBulkPending: boolean = false;
  public similarityBulkResults: SimilarityRerunTask[] = [];
  public sceneReusePending: boolean = false;
  public sceneReuseResults: SimilarityRerunTask[] = [];
  private similarityRerunTrackingCanceled: boolean = false;
  public selectedModelSimilarityRuns: HypersceneSimilarityRunView[] = [];
  public selectedModelSimilarityRunsLoading: boolean = false;
  public selectedModelSimilarityRunsError: string | null = null;
  private selectedModelSimilarityRunsRequestId: number = 0;
  public modelDropdownOpen: boolean = false;
  public sceneModelAction: 'create' | 'attach' | 'instance' | null = null;
  public sceneActionModelId: string | null = null;
  public sceneActionModelIds: string[] = [];
  public sceneActionSceneId: string | null = null;
  public sceneActionNameDraft: string = '';
  public sceneInstanceDrafts: HypersceneSceneModelInstanceDraft[] = [];
  public sceneTransformDraft: HypersceneSceneTransformDraft = this.createSceneTransformDraft();
  public selectedSceneModelInstance: HypersceneSceneModelInstanceView | null = null;
  public selectedSceneModelInstanceLoading: boolean = false;
  public selectedSceneModelInstanceError: string | null = null;
  private selectedSceneModelInstanceRequestId: number = 0;
  public sceneModelActionPending: boolean = false;
  public selectedModelRenameDraft: string = '';
  public scenesRegistry: HypersceneSceneView[] = [];
  public sceneSearchQuery: string = '';
  public selectedSceneId: string | null = null;
  public sceneDropdownOpen: boolean = false;
  public selectedSceneRenameDraft: string = '';
  public artifactRenameTarget: 'model' | 'scene' | null = null;
  public sceneContextAction: 'instances' | 'transform' | null = null;
  public processingsRegistry: HypersceneProcessingView[] = [];
  public selectedProcessingId: string | null = null;
  public selectedProcessingDetails: HypersceneProcessingView | null = null;
  public trackedProcessingId: string | null = null;
  public trackedProcessingStatus: string = 'n/a';
  public trackedProcessingType: string = 'n/a';
  public trackedProcessingLastCheckedAt: string = 'n/a';
  public trackedProcessingHistory: string[] = [];
  public processingsAutoRefreshEnabled: boolean = false;
  public processingsPollingMs: number = 5000;
  public processingsLastRefreshAt: string = 'n/a';
  private processingsRefreshedAtMs: number | null = null;
  private refreshAgeTimer: number | null = null;
  public uploadInProgress: boolean = false;
  public photogrammetryProject: HsPhotogrammetryProject | null = null;
  public photogrammetryModelName: string = '';
  public photogrammetryImportMatrix: string = '1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1';
  public photogrammetryMessage: string = '';
  public photogrammetryError: string | null = null;
  public rvmAttValidationInProgress: boolean = false;
  public rvmAttProject: HsRvmAttProject | null = null;
  public rvmAttMessage: string = '';
  public rvmAttError: string | null = null;
  public artifactsTab: 'models' | 'scenes' | 'processings' = 'models';
  public artifactsImportOpen: boolean = false;
  public artifactsImportSource: 'files' | 'photogrammetry' | 'rvm-att' = 'files';
  public artifactsProcessingsFilter: 'all' | 'active' | 'failed' | 'completed' = 'all';
  public artifactsProcessingsLimit: number = ARTIFACTS_PROCESSINGS_PAGE_SIZE;
  public artifactsConfirm: ArtifactsConfirmation | null = null;
  public artifactsConfirmPending: boolean = false;
  public artifactsCopiedId: string | null = null;
  public sceneActionModelFilter: string = '';
  private modelPickerOptionsCache: { source: HypersceneModelView[]; options: ArtifactPickerOption[] } | null = null;
  private scenePickerOptionsCache: { source: HypersceneSceneView[]; options: ArtifactPickerOption[] } | null = null;

  public get canUploadRvmAtt(): boolean {
    return (
      this.rvmAttProject !== null &&
      this.rvmAttProject.errors.length === 0 &&
      this.rvmAttProject.pairs.some((pair: HsRvmAttPair) => pair.processingId === null) &&
      !this.rvmAttValidationInProgress &&
      !this.uploadInProgress
    );
  }

  public async onRvmAttFolderChanged(event: Event): Promise<void> {
    if (!(event.target instanceof HTMLInputElement) || this.uploadInProgress || this.rvmAttValidationInProgress) {
      return;
    }
    const files: File[] = Array.from(event.target.files ?? []);
    event.target.value = '';
    this.rvmAttProject = null;
    this.rvmAttError = null;
    this.rvmAttMessage = 'Validating RVM files and optional ATT locally; no files are being uploaded...';
    this.rvmAttValidationInProgress = true;
    this.changeDetector.markForCheck();
    try {
      this.rvmAttProject = await hsValidateRvmAttProject(files);
      this.rvmAttMessage =
        this.rvmAttProject.errors.length === 0
          ? 'Folder validated. Each RVM will become a separate model, with matching ATT if present.'
          : 'Fix the errors below and select the folder again. Nothing has been uploaded.';
    } catch (error: unknown) {
      this.rvmAttError = error instanceof Error ? error.message : 'Could not read the selected RVM/ATT folder.';
      this.rvmAttMessage = '';
    } finally {
      this.rvmAttValidationInProgress = false;
      this.changeDetector.markForCheck();
    }
  }

  public async uploadRvmAttProject(): Promise<void> {
    if (this.uploadInProgress || this.rvmAttValidationInProgress) {
      return;
    }
    if (!this.canUploadRvmAtt || this.rvmAttProject === null) {
      this.rvmAttError = 'Select and validate a folder containing RVM files first. Matching ATT files are optional.';
      this.changeDetector.markForCheck();
      return;
    }
    const project: HsRvmAttProject = this.rvmAttProject;
    const pending: HsRvmAttPair[] = project.pairs.filter((pair: HsRvmAttPair) => pair.processingId === null);
    this.uploadInProgress = true;
    this.rvmAttError = null;
    let failed: number = 0;
    try {
      for (const pair of pending) {
        pair.error = null;
        try {
          const filePathInfos: { filePath: string; fileBucket: string; fileName: string }[] = [];
          for (const entry of pair.att === null ? [pair.rvm] : [pair.rvm, pair.att]) {
            this.rvmAttMessage = `Uploading ${entry.path}`;
            this.changeDetector.markForCheck();
            const document: HypersceneDocumentView = await this.ingestAdapter.uploadDocument(
              this.ingestApiBaseUrl,
              entry.file
            );
            filePathInfos.push({ filePath: document.filePath, fileBucket: document.bucket, fileName: entry.file.name });
          }
          pair.processingId = await this.ingestAdapter.parseDocument(this.ingestApiBaseUrl, {
            modelName: pair.name,
            parentVersionId: null,
            parserType: 'rvm',
            filePathInfos,
            metadataInfos: [
              {
                source: this.ingestTag,
                fileName: pair.rvm.path,
                ...(pair.att === null ? {} : { attFileName: pair.att.path }),
                ingestRequestId: getUuid(),
                rvmAtt: true,
              },
            ],
          });
        } catch (error: unknown) {
          pair.error = error instanceof Error ? error.message : 'RVM model upload failed.';
          failed++;
        }
        this.changeDetector.markForCheck();
      }
      const started: number = project.pairs.filter((pair: HsRvmAttPair) => pair.processingId !== null).length;
      this.rvmAttMessage = `Queued ${started}/${project.pairs.length} RVM/ATT models. Track parsing in Processings.`;
      this.rvmAttError =
        failed === 0 ? null : `${failed} models failed. See model errors below; retry uploads only unqueued models.`;
      this.ingestState = {
        ...this.ingestState,
        phase: started > 0 ? 'polling' : 'failed',
        modelId: null,
        processingId: null,
        processingStatus: null,
        parserType: 'rvm',
        fileName: null,
        message: this.rvmAttMessage,
        error: this.rvmAttError,
      };
      await this.refreshProcessingsRegistry();
      await this.refreshModelsRegistry();
    } catch (error: unknown) {
      this.rvmAttError = error instanceof Error ? error.message : 'Could not refresh RVM/ATT processing status.';
      this.setIngestError(this.rvmAttError);
    } finally {
      this.uploadInProgress = false;
      this.changeDetector.markForCheck();
    }
  }

  public get selectedModel(): HypersceneModelView | undefined {
    return this.modelsRegistry.find((model: HypersceneModelView) => model.id === this.selectedModelId);
  }

  public sceneSettingsBusy: boolean = false;
  public sceneSettingsStatus: { kind: 'saved' | 'error'; text: string } | null = null;

  public get selectedSceneSettings(): HypersceneSceneSettings {
    return this.selectedScene?.settings ?? this.viewerSceneSettings ?? {};
  }

  public get selectedSceneQualityProfile(): string {
    return this.selectedSceneSettings.qualityProfile ?? '';
  }

  public get selectedSceneHasStartCamera(): boolean {
    return (this.selectedSceneSettings.startCamera ?? '').length > 0;
  }

  public onSceneQualityProfileChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement) || this.selectedSceneId === null) {
      return;
    }
    const profile: string | null = event.target.value.length > 0 ? event.target.value : null;
    void this.updateSceneSettings(
      this.selectedSceneId,
      { qualityProfile: profile },
      profile === null ? 'Quality profile reset to the viewer default.' : 'Quality profile saved.'
    );
  }

  public clearSceneStartCamera(): void {
    if (this.selectedSceneId !== null) {
      void this.updateSceneSettings(this.selectedSceneId, { startCamera: null }, 'Start camera cleared.');
    }
  }

  private async updateSceneSettings(sceneId: string, patch: HypersceneSceneSettings, savedText: string): Promise<void> {
    if (this.sceneSettingsBusy) {
      return;
    }
    this.sceneSettingsBusy = true;
    this.sceneSettingsStatus = null;
    try {
      // The other field is merged from the server's copy so a stale registry cannot overwrite it.
      const current: HypersceneSceneView = await this.ingestAdapter.getScene(this.ingestApiBaseUrl, sceneId);
      const updated: HypersceneSceneView = await this.ingestAdapter.putSceneSettings(this.ingestApiBaseUrl, sceneId, {
        ...current.settings,
        ...patch,
      });
      const settings: HypersceneSceneSettings | null = updated.settings ?? null;
      this.scenesRegistry = this.scenesRegistry.map((scene: HypersceneSceneView) =>
        scene.id === sceneId ? { ...scene, settings } : scene
      );
      if (this.ingestState.sceneId === sceneId) {
        this.viewerSceneSettings = settings;
      }
      this.sceneSettingsStatus = { kind: 'saved', text: savedText };
    } catch (error: unknown) {
      this.sceneSettingsStatus = {
        kind: 'error',
        text: error instanceof Error ? error.message : 'Failed to save scene settings.',
      };
    } finally {
      this.sceneSettingsBusy = false;
      this.changeDetector.markForCheck();
    }
  }

  public get selectedScene(): HypersceneSceneView | undefined {
    return this.scenesRegistry.find((scene: HypersceneSceneView) => scene.id === this.selectedSceneId);
  }

  public get filteredModelsRegistry(): HypersceneModelView[] {
    return this.filterArtifactRegistry(this.modelsRegistry, this.modelSearchQuery, (model: HypersceneModelView) =>
      this.getModelDisplayName(model)
    );
  }

  public get filteredScenesRegistry(): HypersceneSceneView[] {
    return this.filterArtifactRegistry(this.scenesRegistry, this.sceneSearchQuery, (scene: HypersceneSceneView) =>
      this.getSceneDisplayName(scene)
    );
  }

  public get canUploadModel(): boolean {
    return this.selectedUploadFile !== null && this.ingestParserType.trim().length > 0 && !this.uploadInProgress;
  }

  public get canUploadPhotogrammetry(): boolean {
    return (
      this.photogrammetryProject !== null &&
      this.photogrammetryProject.objCount > 0 &&
      this.photogrammetryModelName.trim().length > 0 &&
      this.getImportMatrixRequest() !== null &&
      !this.uploadInProgress
    );
  }

  public onPhotogrammetryNameChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.photogrammetryModelName = event.target.value;
    }
  }

  public onPhotogrammetryImportMatrixChanged(event: Event): void {
    if (event.target instanceof HTMLTextAreaElement) {
      this.photogrammetryImportMatrix = event.target.value;
      this.photogrammetryError = null;
    }
  }

  private getImportMatrixRequest(): { photogrammetryImportMatrix: number[] } | null {
    const matrix: number[] = this.photogrammetryImportMatrix
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    return matrix.length === 16 && matrix.every(Number.isFinite) ? { photogrammetryImportMatrix: matrix } : null;
  }

  public onPhotogrammetryFolderChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement) || this.uploadInProgress) {
      return;
    }
    const files: File[] = Array.from(event.target.files ?? []);
    event.target.value = '';
    this.photogrammetryProject = hsSelectPhotogrammetryProject(files);
    this.photogrammetryModelName = this.photogrammetryProject.name;
    this.photogrammetryError = null;
    this.photogrammetryMessage =
      this.photogrammetryProject.objCount > 0
        ? 'Folder selected. Files are not validated locally; parsing errors will appear in Processings after upload.'
        : 'Select a folder containing at least one OBJ file.';
    this.changeDetector.markForCheck();
  }

  public async uploadPhotogrammetryProject(): Promise<void> {
    if (this.uploadInProgress) {
      return;
    }
    if (!this.canUploadPhotogrammetry || this.photogrammetryProject === null) {
      this.photogrammetryError = 'Select a folder containing OBJ files and enter a model name first.';
      this.changeDetector.markForCheck();
      return;
    }
    const project: HsPhotogrammetryProject = this.photogrammetryProject;
    const modelName: string = this.photogrammetryModelName.trim();
    const importMatrixRequest: { photogrammetryImportMatrix: number[] } | null = this.getImportMatrixRequest();
    if (importMatrixRequest === null) {
      this.photogrammetryError = 'Enter a valid photogrammetry import matrix.';
      this.changeDetector.markForCheck();
      return;
    }
    this.uploadInProgress = true;
    this.photogrammetryError = null;
    this.changeDetector.markForCheck();
    try {
      const filePathInfos: { filePath: string; fileBucket: string; fileName: string }[] = new Array(
        project.files.length
      );
      let nextFileIndex: number = 0;
      let completedFiles: number = 0;
      let uploadErrorMessage: string | null = null;
      const uploadWorker = async (): Promise<void> => {
        while (nextFileIndex < project.files.length && uploadErrorMessage === null) {
          const index: number = nextFileIndex++;
          const entry: HsPhotogrammetryProjectFile = project.files[index];
          try {
            let document: HypersceneDocumentView | undefined;
            for (let attempt: number = 1; attempt <= 3; attempt++) {
              try {
                document = await this.ingestAdapter.uploadDocument(this.ingestApiBaseUrl, entry.file);
                break;
              } catch (error: unknown) {
                if (attempt === 3) {
                  throw error;
                }
                this.photogrammetryMessage = `Retrying ${entry.path} (${attempt + 1}/3) in 5 seconds...`;
                this.changeDetector.markForCheck();
                await this.delay(5000);
              }
            }
            if (document === undefined) {
              throw new Error(`Upload returned no document for ${entry.path}.`);
            }
            filePathInfos[index] = { filePath: document.filePath, fileBucket: document.bucket, fileName: entry.path };
            completedFiles++;
            this.photogrammetryMessage = `Uploaded ${completedFiles}/${project.files.length} files.`;
            this.changeDetector.markForCheck();
          } catch (error: unknown) {
            uploadErrorMessage ??= error instanceof Error ? error.message : String(error);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, project.files.length) }, () => uploadWorker()));
      if (uploadErrorMessage !== null) {
        throw new Error(uploadErrorMessage);
      }
      this.photogrammetryMessage = 'Starting project parsing...';
      this.changeDetector.markForCheck();
      const processingId: string = await this.ingestAdapter.parseDocument(this.ingestApiBaseUrl, {
        modelName,
        parentVersionId: null,
        parserType: 'obj',
        photogrammetryLod: true,
        ...importMatrixRequest,
        filePathInfos,
        metadataInfos: [
          {
            source: this.ingestTag,
            fileName: modelName,
            modelName,
            ingestRequestId: getUuid(),
            photogrammetry: true,
            objCount: project.objCount,
          },
        ],
      });
      this.ingestState = {
        ...this.ingestState,
        phase: 'polling',
        parserType: 'obj',
        fileName: project.name,
        modelId: null,
        processingId,
        processingStatus: null,
        error: null,
        message: `Started parsing photogrammetry project "${modelName}".`,
      };
      this.photogrammetryMessage = `Project "${modelName}" queued as one model. Track progress in Processings.`;
      // Clear the selection once queued, even if refreshing the lists fails.
      this.photogrammetryProject = null;
      await this.refreshProcessingsRegistry();
      await this.refreshModelsRegistry();
    } catch (error: unknown) {
      this.photogrammetryError = error instanceof Error ? error.message : 'Photogrammetry upload failed.';
      this.photogrammetryMessage = '';
      this.setIngestError(this.photogrammetryError);
    } finally {
      this.uploadInProgress = false;
      this.changeDetector.markForCheck();
    }
  }

  public onUploadParserChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.ingestParserType = event.target.value;
    }
  }

  private updateRefreshAge(): void {
    if (this.processingsRefreshedAtMs === null) {
      this.processingsLastRefreshAt = 'n/a';
      return;
    }
    const seconds: number = Math.max(0, Math.floor((Date.now() - this.processingsRefreshedAtMs) / 1000));
    this.processingsLastRefreshAt = seconds < 60 ? `${seconds} sec ago` : `${Math.floor(seconds / 60)} min ago`;
  }
  private readonly sceneTree: HsSceneTree = new HsSceneTree({
    getSceneRootNodes: (sceneId: string) => this.ingestAdapter.getSceneRootNodes(this.ingestApiBaseUrl, sceneId),
    getNodeChildren: (nodeId: string) => this.ingestAdapter.getNodeChildren(this.ingestApiBaseUrl, nodeId),
    getNodeChildrenBulk: (nodeIds: readonly string[]) =>
      this.ingestAdapter.getNodeChildrenBulk(this.ingestApiBaseUrl, [...nodeIds]),
    getNodeRoute: (nodeId: string) => this.ingestAdapter.getNodeRoute(this.ingestApiBaseUrl, nodeId),
    getNodesVisibility: (sceneId: string, nodeIds: readonly string[]) =>
      this.ingestAdapter.getSceneNodesVisibility(this.ingestApiBaseUrl, sceneId, [...nodeIds]),
  });

  public get sceneTreeRoots(): HypersceneSceneTreeNode[] {
    return this.sceneTree.roots;
  }

  public set sceneTreeRoots(value: HypersceneSceneTreeNode[]) {
    this.sceneTree.roots = value;
  }
  public selectedSceneNodeId: string | null = null;
  public selectedSceneNodeTitle: string | null = null;
  public ingestState: HypersceneIngestState = {
    phase: 'idle',
    message: '',
    fileName: null,
    parserType: 'ifc',
    documentId: null,
    processingId: null,
    modelId: null,
    rootNodeId: null,
    sceneId: null,
    sceneRootNodesCount: 0,
    geometryIdsCount: 0,
    meshGeometryIdsCount: 0,
    lineGeometryIdsCount: 0,
    pointCloudGeometryIdsCount: 0,
    renderedMeshObjectsCount: 0,
    renderedLineObjectsCount: 0,
    renderedPointCloudObjectsCount: 0,
    collidedPointsCount: 0,
    meshesBinaryBytes: 0,
    linesBinaryBytes: 0,
    pointCloudsBinaryBytes: 0,
    processingStatus: null,
    error: null,
  };

  private readonly ingestAdapter: HypersceneIngestAdapterService = new HypersceneIngestAdapterService();
  private selectedUploadFile: File | null = null;
  private selectedUploadFiles: File[] = [];
  private isPollingCanceled: boolean = false;
  private processingsAutoRefreshTimer: number | null = null;
  private viewerSceneSettings: HypersceneSceneSettings | null = null;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  public ngAfterViewInit(): void {
    this.refreshAgeTimer = window.setInterval(() => {
      this.updateRefreshAge();
      this.changeDetector.markForCheck();
    }, 1000);
    void this.refreshScenesRegistry();
    void this.refreshModelsRegistry();
    void this.refreshProcessingsRegistry();
  }

  public get activeProcessingsCount(): number {
    return this.processingsRegistry.filter(
      (processing: HypersceneProcessingView) => !this.isTerminalProcessingStatus(processing.status)
    ).length;
  }

  public get filteredArtifactsProcessings(): HypersceneProcessingView[] {
    const matches = (processing: HypersceneProcessingView): boolean => {
      switch (this.artifactsProcessingsFilter) {
        case 'active':
          return !this.isTerminalProcessingStatus(processing.status);
        case 'failed':
          return processing.status === 'Failed' || processing.status === 'Canceled';
        case 'completed':
          return processing.status === 'Completed';
        default:
          return true;
      }
    };
    const matching: HypersceneProcessingView[] = this.processingsRegistry.filter(matches);
    // Keep running work on top, matching the order of the unfiltered list.
    return [
      ...matching.filter((processing: HypersceneProcessingView) => !this.isTerminalProcessingStatus(processing.status)),
      ...matching.filter((processing: HypersceneProcessingView) => this.isTerminalProcessingStatus(processing.status)),
    ];
  }

  public get displayedArtifactsProcessings(): HypersceneProcessingView[] {
    return this.filteredArtifactsProcessings.slice(0, this.artifactsProcessingsLimit);
  }

  public get areAllVisibleModelsSelectedForSimilarity(): boolean {
    const visible: HypersceneModelView[] = this.filteredModelsRegistry;
    return (
      visible.length > 0 &&
      visible.every((model: HypersceneModelView) => this.similarityBulkModelIds.includes(model.id))
    );
  }

  public setArtifactsTab(tab: 'models' | 'scenes' | 'processings'): void {
    this.artifactsTab = tab;
    this.changeDetector.markForCheck();
  }

  public onArtifactsTabKeydown(event: KeyboardEvent): void {
    const tabs: ('models' | 'scenes' | 'processings')[] = ['models', 'scenes', 'processings'];
    const index: number = tabs.indexOf(this.artifactsTab);
    const nextIndex: number | null =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
        ? (index + tabs.length - 1) % tabs.length
        : event.key === 'Home'
        ? 0
        : event.key === 'End'
        ? tabs.length - 1
        : null;
    if (nextIndex === null) {
      return;
    }
    event.preventDefault();
    this.setArtifactsTab(tabs[nextIndex]);
    this.document.getElementById(`artifacts-tab-${tabs[nextIndex]}`)?.focus();
  }

  public openArtifactsImport(source: 'files' | 'photogrammetry' | 'rvm-att' = this.artifactsImportSource): void {
    this.artifactsImportSource = source;
    this.artifactsImportOpen = true;
    this.changeDetector.markForCheck();
  }

  public closeArtifactsImport(): void {
    this.artifactsImportOpen = false;
    this.changeDetector.markForCheck();
  }

  // Cached per registry array so pickers get a stable input between refreshes.
  public get modelPickerOptions(): ArtifactPickerOption[] {
    if (this.modelPickerOptionsCache?.source !== this.modelsRegistry) {
      this.modelPickerOptionsCache = {
        source: this.modelsRegistry,
        options: this.modelsRegistry.map((model: HypersceneModelView) => ({
          value: model.id,
          label: this.getModelTitle(model),
          meta: `${model.id.slice(0, 6)} · ${this.getModelDisplayDate(model)}`,
        })),
      };
    }
    return this.modelPickerOptionsCache.options;
  }

  public get scenePickerOptions(): ArtifactPickerOption[] {
    if (this.scenePickerOptionsCache?.source !== this.scenesRegistry) {
      this.scenePickerOptionsCache = {
        source: this.scenesRegistry,
        options: this.scenesRegistry.map((scene: HypersceneSceneView) => ({
          value: scene.id,
          label: this.getSceneDisplayName(scene),
          meta: `${scene.id.slice(0, 6)} · ${this.getSceneDisplayDate(scene)}`,
        })),
      };
    }
    return this.scenePickerOptionsCache.options;
  }

  public get filteredSceneActionModels(): HypersceneModelView[] {
    const query: string = this.sceneActionModelFilter.trim().toLocaleLowerCase();
    if (query.length === 0) {
      return this.modelsRegistry;
    }
    return this.modelsRegistry.filter((model: HypersceneModelView) =>
      `${this.getModelTitle(model)} ${model.id} ${this.getModelDisplayDate(model)}`.toLocaleLowerCase().includes(query)
    );
  }

  public onSceneActionModelFilterChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.sceneActionModelFilter = event.target.value;
      this.changeDetector.markForCheck();
    }
  }

  public setSceneActionScene(sceneId: string | null): void {
    this.sceneActionSceneId = sceneId;
    this.changeDetector.markForCheck();
  }

  public openArtifactsSceneAction(action: 'create' | 'attach' | 'instance', modelId?: string): void {
    // The dialog shows ingest errors, so an error left over from an earlier action must not appear there.
    this.ingestState = { ...this.ingestState, error: null };
    this.sceneActionModelFilter = '';
    if (modelId === undefined) {
      this.openSceneModelAction(action);
    } else {
      this.openModelSceneAction(modelId, action);
    }
    this.changeDetector.markForCheck();
  }

  public dismissArtifactsNotice(): void {
    this.ingestState = { ...this.ingestState, message: '', error: null };
    this.changeDetector.markForCheck();
  }

  public showModelInArtifacts(modelId: string): void {
    this.artifactsTab = 'models';
    if (
      this.modelSearchQuery.length > 0 &&
      !this.filteredModelsRegistry.some((model: HypersceneModelView) => model.id === modelId)
    ) {
      this.modelSearchQuery = '';
    }
    this.selectModelFromDropdown(modelId);
    this.scrollSelectedArtifactIntoView();
  }

  public showSceneInArtifacts(sceneId: string): void {
    this.artifactsTab = 'scenes';
    if (
      this.sceneSearchQuery.length > 0 &&
      !this.filteredScenesRegistry.some((scene: HypersceneSceneView) => scene.id === sceneId)
    ) {
      this.sceneSearchQuery = '';
    }
    this.selectSceneFromDropdown(sceneId);
    this.scrollSelectedArtifactIntoView();
  }

  public revealArtifactDetailOnNarrowScreens(): void {
    // Below this width the detail panel stacks under the list, so a selection would otherwise happen off-screen.
    if (window.matchMedia('(max-width: 960px)').matches) {
      window.setTimeout(() => {
        this.document.querySelector('.artifacts-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  private scrollSelectedArtifactIntoView(): void {
    // The target row only exists after the tab switch renders.
    window.setTimeout(() => {
      this.document.querySelector('.artifacts-row--selected')?.scrollIntoView({ block: 'nearest' });
    });
  }

  public isModelInRegistry(modelId: string | null | undefined): boolean {
    return (
      modelId !== null &&
      modelId !== undefined &&
      this.modelsRegistry.some((model: HypersceneModelView) => model.id === modelId)
    );
  }

  public getModelTitle(model: HypersceneModelView): string {
    return this.tryExtractModelDisplayName(model.metadata) ?? 'Unnamed model';
  }

  public getModelTitleById(modelId: string): string {
    if (modelId.startsWith('scene:')) {
      const sceneId: string = modelId.slice('scene:'.length);
      const scene: HypersceneSceneView | undefined = this.scenesRegistry.find(
        (item: HypersceneSceneView) => item.id === sceneId
      );
      return scene === undefined ? modelId : `Scene ${this.getSceneDisplayName(scene)}`;
    }
    const model: HypersceneModelView | undefined = this.modelsRegistry.find(
      (item: HypersceneModelView) => item.id === modelId
    );
    return model === undefined ? modelId : this.getModelTitle(model);
  }

  public getSceneTitleById(sceneId: string): string {
    const scene: HypersceneSceneView | undefined = this.scenesRegistry.find(
      (item: HypersceneSceneView) => item.id === sceneId
    );
    return scene === undefined ? sceneId : this.getSceneDisplayName(scene);
  }

  public toggleAllVisibleModelsForSimilarity(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    const visibleIds: string[] = this.filteredModelsRegistry.map((model: HypersceneModelView) => model.id);
    this.similarityBulkModelIds = event.target.checked
      ? Array.from(new Set([...this.similarityBulkModelIds, ...visibleIds]))
      : this.similarityBulkModelIds.filter((id: string) => !visibleIds.includes(id));
    this.changeDetector.markForCheck();
  }

  public clearSimilarityBulkSelection(): void {
    this.similarityBulkModelIds = [];
    this.changeDetector.markForCheck();
  }

  public selectSceneRootNode(nodeId: string): void {
    if (this.sceneModelActionPending) {
      return;
    }
    this.selectedSceneNodeId = this.selectedSceneNodeId === nodeId && this.sceneContextAction !== null ? null : nodeId;
    this.sceneContextAction = this.selectedSceneNodeId === null ? null : 'instances';
    this.sceneTransformDraft = this.createSceneTransformDraft();
    if (this.selectedSceneNodeId === null) {
      this.clearSelectedSceneModelInstance();
    } else {
      void this.loadSelectedSceneModelInstance();
    }
    this.changeDetector.markForCheck();
  }

  public setArtifactsProcessingsFilter(filter: 'all' | 'active' | 'failed' | 'completed'): void {
    this.artifactsProcessingsFilter = filter;
    this.artifactsProcessingsLimit = ARTIFACTS_PROCESSINGS_PAGE_SIZE;
    this.changeDetector.markForCheck();
  }

  public showMoreArtifactsProcessings(): void {
    this.artifactsProcessingsLimit += ARTIFACTS_PROCESSINGS_PAGE_SIZE;
    this.changeDetector.markForCheck();
  }

  public toggleProcessingSelection(processing: HypersceneProcessingView): void {
    if (this.selectedProcessingId === processing.id) {
      this.selectedProcessingId = null;
      this.selectedProcessingDetails = null;
    } else {
      this.selectProcessing(processing);
    }
    this.changeDetector.markForCheck();
  }

  public countProcessingsByFilter(filter: 'all' | 'active' | 'failed' | 'completed'): number {
    switch (filter) {
      case 'active':
        return this.activeProcessingsCount;
      case 'failed':
        return this.processingsRegistry.filter(
          (item: HypersceneProcessingView) => item.status === 'Failed' || item.status === 'Canceled'
        ).length;
      case 'completed':
        return this.processingsRegistry.filter((item: HypersceneProcessingView) => item.status === 'Completed').length;
      default:
        return this.processingsRegistry.length;
    }
  }

  public requestModelDelete(model: HypersceneModelView): void {
    this.artifactsConfirm = {
      title: 'Delete model?',
      message: `"${this.getModelTitle(model)}" (${
        model.id
      }) will be permanently deleted. Scenes that use it lose its geometry.`,
      confirmLabel: 'Delete model',
      run: () => this.deleteModelFromRegistry(model.id),
    };
    this.changeDetector.markForCheck();
  }

  public requestSceneDelete(scene: HypersceneSceneView): void {
    this.artifactsConfirm = {
      title: 'Delete scene?',
      message: `"${this.getSceneDisplayName(scene)}" (${scene.id}) will be permanently deleted. Its models are kept.`,
      confirmLabel: 'Delete scene',
      run: () => this.deleteSceneFromRegistry(scene.id),
    };
    this.changeDetector.markForCheck();
  }

  public requestSceneRootRemoval(): void {
    const root: HypersceneSceneTreeNode | undefined = this.sceneTreeRoots.find(
      (node: HypersceneSceneTreeNode) => node.id === this.selectedSceneNodeId
    );
    if (root === undefined) {
      return;
    }
    const isInstance: boolean = this.selectedSceneModelInstance !== null;
    this.artifactsConfirm = {
      title: isInstance ? 'Remove instance from scene?' : 'Remove model from scene?',
      message: `"${root.title}" will be removed from this scene. The model itself is kept.`,
      confirmLabel: isInstance ? 'Remove instance' : 'Remove model',
      run: () => this.removeSelectedRootNodeFromScene(),
    };
    this.changeDetector.markForCheck();
  }

  public requestProcessingsClear(): void {
    const count: number = this.getClearableProcessingsCount();
    if (count === 0) {
      return;
    }
    this.artifactsConfirm = {
      title: 'Clear finished processings?',
      message: `${count} completed, failed or canceled ${
        count === 1 ? 'record' : 'records'
      } will be removed from the list.`,
      confirmLabel: 'Clear',
      run: () => this.clearProcessingsRegistry(),
    };
    this.changeDetector.markForCheck();
  }

  public async confirmArtifactsAction(): Promise<void> {
    const confirmation: ArtifactsConfirmation | null = this.artifactsConfirm;
    if (confirmation === null || this.artifactsConfirmPending) {
      return;
    }
    this.artifactsConfirmPending = true;
    this.changeDetector.markForCheck();
    try {
      await confirmation.run();
    } finally {
      this.artifactsConfirmPending = false;
      this.artifactsConfirm = null;
      this.changeDetector.markForCheck();
    }
  }

  public cancelArtifactsConfirm(): void {
    if (!this.artifactsConfirmPending) {
      this.artifactsConfirm = null;
      this.changeDetector.markForCheck();
    }
  }

  public onArtifactsEscape(): void {
    if (this.artifactsConfirm !== null) {
      this.cancelArtifactsConfirm();
    } else if (this.sceneModelAction !== null) {
      if (!this.sceneModelActionPending) {
        this.closeSceneModelAction();
      }
    } else if (this.artifactsImportOpen) {
      this.closeArtifactsImport();
    } else if (this.artifactRenameTarget !== null) {
      this.cancelArtifactRename();
    }
    this.changeDetector.markForCheck();
  }

  public async copyArtifactId(id: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(id);
      this.artifactsCopiedId = id;
    } catch {
      this.artifactsCopiedId = null;
    }
    this.changeDetector.markForCheck();
    window.setTimeout(() => {
      if (this.artifactsCopiedId === id) {
        this.artifactsCopiedId = null;
        this.changeDetector.markForCheck();
      }
    }, 1500);
  }

  public ngOnDestroy(): void {
    if (this.refreshAgeTimer !== null) {
      window.clearInterval(this.refreshAgeTimer);
      this.refreshAgeTimer = null;
    }
    this.isPollingCanceled = true;
    this.similarityRerunTrackingCanceled = true;
    if (this.processingsAutoRefreshTimer !== null) {
      window.clearInterval(this.processingsAutoRefreshTimer);
      this.processingsAutoRefreshTimer = null;
    }
  }

  public async refreshModelsRegistry(): Promise<void> {
    try {
      const models: HypersceneModelView[] = await this.ingestAdapter.filterModels(
        this.ingestApiBaseUrl,
        this.getRegistryMetadataFilter()
      );
      const sortedModels: HypersceneModelView[] = this.sortModelsByCreatedAt(models);
      this.modelsRegistry = sortedModels;
      this.similarityBulkModelIds = this.similarityBulkModelIds.filter((id: string) =>
        sortedModels.some((model: HypersceneModelView) => model.id === id)
      );
      if (
        this.selectedModelId !== null &&
        !sortedModels.some((model: HypersceneModelView) => model.id === this.selectedModelId)
      ) {
        this.selectedModelId = null;
      }
      if (this.selectedModelId === null) {
        this.selectedModelId = sortedModels[0]?.id ?? null;
      }
      this.syncSelectedModelRenameDraft();
      await Promise.all([this.refreshSelectedModelScenes(), this.refreshSelectedModelSimilarityRuns()]);
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load models registry.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public async refreshScenesRegistry(): Promise<void> {
    try {
      const scenes: HypersceneSceneView[] = await this.ingestAdapter.filterScenes(
        this.ingestApiBaseUrl,
        this.getRegistryMetadataFilter()
      );
      const sortedScenes: HypersceneSceneView[] = this.sortScenesByCreatedAt(scenes);
      this.scenesRegistry = sortedScenes;
      if (
        this.selectedSceneId !== null &&
        !sortedScenes.some((scene: HypersceneSceneView) => scene.id === this.selectedSceneId)
      ) {
        this.selectedSceneId = null;
      }

      this.syncSelectedSceneRenameDraft();
      await this.refreshArtifactSceneRoots(this.selectedSceneId);
      await this.refreshSelectedModelScenes();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load scenes registry.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public async refreshProcessingsRegistry(): Promise<void> {
    try {
      const processings: HypersceneProcessingView[] = await this.ingestAdapter.filterProcessings(
        this.ingestApiBaseUrl,
        this.getRegistryMetadataFilter()
      );
      processings.sort((first: HypersceneProcessingView, second: HypersceneProcessingView) => {
        const timeDifference: number =
          (this.getProcessingStartedAt(second) ?? -Infinity) - (this.getProcessingStartedAt(first) ?? -Infinity);
        return Number.isNaN(timeDifference) || timeDifference === 0
          ? first.id.localeCompare(second.id)
          : timeDifference;
      });
      this.processingsRegistry = processings;
      this.modelsRegistry = this.sortModelsByCreatedAt(this.modelsRegistry);
      if (
        this.selectedProcessingId !== null &&
        !processings.some((processing: HypersceneProcessingView) => processing.id === this.selectedProcessingId)
      ) {
        this.selectedProcessingId = null;
      }
      if (this.selectedProcessingId === null) {
        this.selectedProcessingId = processings[0]?.id ?? null;
      }
      const selectedProcessingId: string | null = this.selectedProcessingId;
      this.selectedProcessingDetails =
        selectedProcessingId === null
          ? null
          : processings.find((processing: HypersceneProcessingView) => processing.id === selectedProcessingId) ?? null;
      this.syncTrackedConversionStatusFromRegistry(processings);
      this.processingsRefreshedAtMs = Date.now();
      this.updateRefreshAge();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load processings registry.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public onModelSearchChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.modelSearchQuery = event.target.value;
      this.changeDetector.markForCheck();
    }
  }

  public selectModelFromDropdown(modelId: string): void {
    this.selectedModelId = modelId;
    this.modelDropdownOpen = false;

    this.syncSelectedModelRenameDraft();
    void this.refreshSelectedModelScenes();
    void this.refreshSelectedModelSimilarityRuns();
    this.changeDetector.markForCheck();
  }

  public async refreshSelectedModelScenes(): Promise<void> {
    const modelId: string | null = this.selectedModelId;
    const requestId: number = ++this.selectedModelScenesRequestId;
    this.selectedModelScenes = [];
    this.selectedModelScenesError = null;
    this.selectedModelScenesLoading = modelId !== null;
    this.changeDetector.markForCheck();
    if (modelId === null) {
      return;
    }

    try {
      const scenes: HypersceneModelSceneParticipationView[] = await this.ingestAdapter.getModelScenes(
        this.ingestApiBaseUrl,
        modelId
      );
      if (requestId === this.selectedModelScenesRequestId && modelId === this.selectedModelId) {
        this.selectedModelScenes = scenes;
      }
    } catch (error: unknown) {
      if (requestId === this.selectedModelScenesRequestId && modelId === this.selectedModelId) {
        this.selectedModelScenesError = error instanceof Error ? error.message : 'Failed to load model scenes.';
      }
    } finally {
      if (requestId === this.selectedModelScenesRequestId && modelId === this.selectedModelId) {
        this.selectedModelScenesLoading = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  public onSimilarityFullRerunChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.similarityFullRerun = event.target.checked;
      this.changeDetector.markForCheck();
    }
  }

  public onSimilarityBulkModelChanged(modelId: string, event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.similarityBulkModelIds = event.target.checked
      ? Array.from(new Set([...this.similarityBulkModelIds, modelId]))
      : this.similarityBulkModelIds.filter((id: string) => id !== modelId);
    this.changeDetector.markForCheck();
  }

  public async refreshSelectedModelSimilarityRuns(): Promise<void> {
    const modelId: string | null = this.selectedModelId;
    const requestId: number = ++this.selectedModelSimilarityRunsRequestId;
    this.selectedModelSimilarityRuns = [];
    this.selectedModelSimilarityRunsError = null;
    this.selectedModelSimilarityRunsLoading = modelId !== null;
    this.changeDetector.markForCheck();
    if (modelId === null) {
      return;
    }

    try {
      const runs: HypersceneSimilarityRunView[] = await this.ingestAdapter.getModelSimilarityRuns(
        this.ingestApiBaseUrl,
        modelId
      );
      if (requestId === this.selectedModelSimilarityRunsRequestId && modelId === this.selectedModelId) {
        this.selectedModelSimilarityRuns = runs;
      }
    } catch (error: unknown) {
      if (requestId === this.selectedModelSimilarityRunsRequestId && modelId === this.selectedModelId) {
        this.selectedModelSimilarityRunsError =
          error instanceof Error ? error.message : 'Failed to load similarity runs.';
      }
    } finally {
      if (requestId === this.selectedModelSimilarityRunsRequestId && modelId === this.selectedModelId) {
        this.selectedModelSimilarityRunsLoading = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  public onSceneSearchChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.sceneSearchQuery = event.target.value;
      this.changeDetector.markForCheck();
    }
  }

  public openModelSceneAction(modelId: string, action: 'create' | 'attach' | 'instance'): void {
    this.selectModelFromDropdown(modelId);
    this.openSceneModelAction(action);
    this.sceneActionModelId = modelId;
    if (action === 'attach') {
      this.sceneActionModelIds = [modelId];
    }
    if (action === 'create') {
      this.sceneActionNameDraft = this.resolveSelectedModelFileName(modelId) ?? '';
    } else if (action === 'instance') {
      const modelName: string = this.resolveSelectedModelFileName(modelId) ?? 'model';
      this.sceneInstanceDrafts = [this.createSceneModelInstanceDraft(modelId, `${modelName} instance`)];
    }
  }

  public selectSceneFromDropdown(sceneId: string | null): void {
    this.sceneDropdownOpen = false;

    this.selectedSceneId = sceneId;
    this.clearSelectedSceneModelInstance();
    this.sceneContextAction = null;
    if (this.artifactRenameTarget === 'scene') {
      this.artifactRenameTarget = null;
    }
    if (this.sceneModelAction === 'attach' || this.sceneModelAction === 'instance') {
      this.sceneActionSceneId = sceneId;
    }
    this.syncSelectedSceneRenameDraft();
    void this.refreshArtifactSceneRoots(this.selectedSceneId);
    this.changeDetector.markForCheck();
  }

  public openSceneModelTransformAction(): void {
    if (
      this.selectedSceneNodeId === null ||
      this.sceneModelActionPending ||
      this.selectedSceneModelInstanceLoading ||
      this.selectedSceneModelInstance === null
    ) {
      return;
    }

    this.sceneContextAction = 'transform';
    this.sceneTransformDraft = this.sceneTransformDraftFromInstance(this.selectedSceneModelInstance);
    this.changeDetector.markForCheck();
  }

  public openSceneInViewer(sceneId: string): void {
    void this.router.navigate(['/viewer'], {
      queryParams: { sceneId },
    });
  }

  public beginModelRename(modelId: string): void {
    this.selectModelFromDropdown(modelId);
    this.artifactRenameTarget = 'model';
  }

  public beginSceneRename(sceneId: string): void {
    this.selectSceneFromDropdown(sceneId);
    this.artifactRenameTarget = 'scene';
  }

  public cancelArtifactRename(): void {
    this.artifactRenameTarget = null;
  }

  private clearSelectedSceneModelInstance(): void {
    ++this.selectedSceneModelInstanceRequestId;
    this.selectedSceneModelInstance = null;
    this.selectedSceneModelInstanceLoading = false;
    this.selectedSceneModelInstanceError = null;
  }

  private isMatchingSceneModelInstance(
    instance: HypersceneSceneModelInstanceView,
    sceneId: string,
    rootNodeId: string
  ): boolean {
    return (
      instance.sceneId === sceneId &&
      instance.rootNodeId === rootNodeId &&
      typeof instance.sourceModelId === 'string' &&
      instance.sourceModelId.length > 0 &&
      typeof instance.sourceVersionId === 'string' &&
      instance.sourceVersionId.length > 0 &&
      typeof instance.sourceRootNodeId === 'string' &&
      instance.sourceRootNodeId.length > 0 &&
      Array.isArray(instance.relativeTransform) &&
      instance.relativeTransform.length === 9 &&
      instance.relativeTransform.every((value: number) => Number.isFinite(value)) &&
      Array.isArray(instance.relativeMatrix) &&
      instance.relativeMatrix.length === 16 &&
      instance.relativeMatrix.every((value: number) => Number.isFinite(value))
    );
  }

  private sceneTransformDraftFromInstance(instance: HypersceneSceneModelInstanceView): HypersceneSceneTransformDraft {
    const [tx, ty, tz, rx, ry, rz, sx, sy, sz] = instance.relativeTransform;
    return {
      translation: { x: tx, y: ty, z: tz },
      rotation: { x: rx, y: ry, z: rz },
      scale: { x: sx, y: sy, z: sz },
    };
  }

  public async loadSelectedSceneModelInstance(): Promise<void> {
    const sceneId: string | null = this.selectedSceneId;
    const rootNodeId: string | null = this.selectedSceneNodeId;
    this.clearSelectedSceneModelInstance();
    const requestId: number = this.selectedSceneModelInstanceRequestId;
    this.sceneTransformDraft = this.createSceneTransformDraft();
    if (sceneId === null || rootNodeId === null) {
      this.changeDetector.markForCheck();
      return;
    }
    this.selectedSceneModelInstanceLoading = true;
    this.changeDetector.markForCheck();
    try {
      const instance: HypersceneSceneModelInstanceView | null = await this.ingestAdapter.getSceneModelInstance(
        this.ingestApiBaseUrl,
        sceneId,
        rootNodeId
      );
      if (
        requestId === this.selectedSceneModelInstanceRequestId &&
        sceneId === this.selectedSceneId &&
        rootNodeId === this.selectedSceneNodeId
      ) {
        if (instance !== null && !this.isMatchingSceneModelInstance(instance, sceneId, rootNodeId)) {
          throw new Error('Invalid scene model instance response.');
        }
        this.selectedSceneModelInstance = instance;
        if (instance !== null) {
          this.sceneTransformDraft = this.sceneTransformDraftFromInstance(instance);
        }
      }
    } catch (error: unknown) {
      if (
        requestId === this.selectedSceneModelInstanceRequestId &&
        sceneId === this.selectedSceneId &&
        rootNodeId === this.selectedSceneNodeId
      ) {
        this.selectedSceneModelInstanceError =
          error instanceof Error ? error.message : 'Failed to load scene model instance.';
      }
    } finally {
      if (
        requestId === this.selectedSceneModelInstanceRequestId &&
        sceneId === this.selectedSceneId &&
        rootNodeId === this.selectedSceneNodeId
      ) {
        this.selectedSceneModelInstanceLoading = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  public onSceneTransformChanged(
    group: 'translation' | 'rotation' | 'scale',
    axis: 'x' | 'y' | 'z',
    event: Event
  ): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const value: number = Number(event.target.value);
    if (!Number.isFinite(value)) {
      return;
    }

    this.sceneTransformDraft[group][axis] = value;
  }

  public async transformSelectedSceneModel(): Promise<void> {
    const sceneId: string | null = this.selectedSceneId;
    const rootNodeId: string | null = this.selectedSceneNodeId;
    if (
      sceneId === null ||
      rootNodeId === null ||
      this.selectedSceneModelInstanceLoading ||
      this.selectedSceneModelInstance === null ||
      !this.isMatchingSceneModelInstance(this.selectedSceneModelInstance, sceneId, rootNodeId)
    ) {
      this.setIngestError('Select a model instance to transform.');
      return;
    }

    this.sceneModelActionPending = true;
    try {
      const transformedRootNode: HypersceneNodeView = await this.ingestAdapter.transformSceneModelInstance(
        this.ingestApiBaseUrl,
        sceneId,
        rootNodeId,
        this.sceneTransformDraft
      );
      this.selectedSceneNodeId = transformedRootNode.id;
      this.selectedSceneNodeTitle = transformedRootNode.title;
      this.ingestState = {
        ...this.ingestState,
        sceneId,
        rootNodeId: transformedRootNode.id,
        message: `Transformed model instance in scene ${sceneId}.`,
        error: null,
      };
      this.sceneContextAction = 'instances';
      await this.refreshArtifactSceneRoots(sceneId);
      await this.loadSelectedSceneModelInstance();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to transform scene model.';
      this.setIngestError(errorMessage);
    } finally {
      this.sceneModelActionPending = false;
      this.changeDetector.markForCheck();
    }
  }

  public onSelectedModelRenameDraftChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.selectedModelRenameDraft = event.target.value;
  }

  public onSelectedSceneRenameDraftChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.selectedSceneRenameDraft = event.target.value;
  }

  public async renameSelectedModel(): Promise<void> {
    if (this.selectedModelId === null) {
      this.setIngestError('Select model to rename.');
      return;
    }

    const modelName: string = this.selectedModelRenameDraft.trim();
    if (modelName.length === 0) {
      this.setIngestError('Provide model name before renaming.');
      return;
    }

    const model: HypersceneModelView | undefined = this.modelsRegistry.find(
      (modelView: HypersceneModelView) => modelView.id === this.selectedModelId
    );

    try {
      const metadataPayload: unknown[] = this.buildRenamedMetadataPayload(model?.metadata, 'modelName', modelName);
      await this.ingestAdapter.putModelMetadata(this.ingestApiBaseUrl, this.selectedModelId, metadataPayload);
      await this.refreshModelsRegistry();
      this.syncSelectedModelRenameDraft();
      this.ingestState = {
        ...this.ingestState,
        message: `Model ${this.selectedModelId} renamed to ${modelName}.`,
        error: null,
      };
      this.artifactRenameTarget = null;
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to rename model.';
      this.setIngestError(errorMessage);
    }
  }

  public async deleteSelectedModelFromRegistry(): Promise<void> {
    if (this.selectedModelId === null) {
      return;
    }

    try {
      const removedModelId: string = this.selectedModelId;
      await this.ingestAdapter.deleteModel(this.ingestApiBaseUrl, removedModelId);
      this.selectedModelId = null;
      await this.refreshModelsRegistry();

      if (this.ingestState.modelId === removedModelId) {
        this.ingestState = {
          ...this.ingestState,
          modelId: null,
          rootNodeId: null,
          message: `Model ${removedModelId} deleted.`,
          error: null,
        };
      } else {
        this.ingestState = {
          ...this.ingestState,
          message: `Model ${removedModelId} deleted.`,
          error: null,
        };
      }

      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to delete selected model.';
      this.setIngestError(errorMessage);
    }
  }

  public async deleteModelFromRegistry(modelId: string): Promise<void> {
    this.selectModelFromDropdown(modelId);
    await this.deleteSelectedModelFromRegistry();
  }

  public async reparseSelectedModel(): Promise<void> {
    if (this.selectedModelId === null || this.uploadInProgress || this.similarityBulkPending) {
      return;
    }

    const modelId: string = this.selectedModelId;
    this.uploadInProgress = true;
    this.isPollingCanceled = false;
    try {
      const processingId: string = await this.ingestAdapter.reparseModel(this.ingestApiBaseUrl, modelId);
      this.ingestState = {
        ...this.ingestState,
        phase: 'polling',
        modelId,
        processingId,
        processingStatus: null,
        message: `Reparsing model ${modelId}...`,
        error: null,
      };
      this.changeDetector.markForCheck();

      const processing: HypersceneProcessingView = await this.waitProcessingCompleted(processingId);
      if (processing.status !== 'Completed') {
        this.setIngestError(`Reparse finished with status: ${processing.status}`);
        return;
      }

      const currentRootNode: HypersceneNodeView = await this.ingestAdapter.getRootNode(this.ingestApiBaseUrl, modelId);
      this.ingestState = {
        ...this.ingestState,
        phase: 'ready',
        modelId,
        rootNodeId: currentRootNode.id,
        processingStatus: processing.status,
        message: 'Model reparsed; affected scenes were updated.',
        error: null,
      };
      await Promise.all([
        this.refreshModelsRegistry(),
        this.refreshScenesRegistry(),
        this.refreshProcessingsRegistry(),
      ]);
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to reparse the selected model.';
      this.setIngestError(errorMessage);
    } finally {
      this.uploadInProgress = false;
      this.changeDetector.markForCheck();
    }
  }

  public async reparseModelFromRegistry(modelId: string): Promise<void> {
    this.selectModelFromDropdown(modelId);
    await this.reparseSelectedModel();
  }

  public async rerunSimilarityForSelectedModels(): Promise<void> {
    if (this.similarityBulkPending || this.uploadInProgress) {
      return;
    }

    const modelIds: string[] = this.similarityBulkModelIds.filter((id: string) =>
      this.modelsRegistry.some((model: HypersceneModelView) => model.id === id)
    );
    if (modelIds.length === 0) {
      this.setIngestError('Select models for similarity rerun.');
      return;
    }
    if (
      modelIds.some((id: string) =>
        this.similarityBulkResults.some(
          (task: SimilarityRerunTask) =>
            task.modelId === id && !['Completed', 'Failed', 'Canceled', 'Error'].includes(task.status)
        )
      )
    ) {
      this.setIngestError('A selected model already has an active similarity rerun.');
      return;
    }
    const restoreExistingReplacements: boolean = this.similarityFullRerun;
    this.similarityBulkPending = true;
    const tasks: SimilarityRerunTask[] = modelIds.map((modelId: string) => ({ modelId, status: 'Starting' }));
    this.similarityBulkResults = [...tasks, ...this.similarityBulkResults];
    this.changeDetector.markForCheck();
    try {
      await Promise.all(
        tasks.map(async (task: SimilarityRerunTask) => {
          try {
            const processingId: string = await this.ingestAdapter.rerunModelSimilarity(
              this.ingestApiBaseUrl,
              task.modelId,
              restoreExistingReplacements
            );
            if (this.similarityRerunTrackingCanceled) return;
            task.processingId = processingId;
            task.status = 'Queued';
            void this.trackSimilarityRerun(task, processingId);
          } catch (error: unknown) {
            task.status = 'Error';
            task.error = error instanceof Error ? error.message : 'Failed to queue similarity rerun.';
          }
          this.changeDetector.markForCheck();
        })
      );
      if (this.similarityRerunTrackingCanceled) return;
      const queued: number = tasks.filter((task: SimilarityRerunTask) => task.processingId !== undefined).length;
      const failed: number = tasks.length - queued;
      if (failed > 0) {
        this.setIngestError(
          `Queued similarity for ${queued} of ${tasks.length} models; ${failed} failed. See results below.`
        );
      } else {
        this.ingestState = {
          ...this.ingestState,
          message: `Queued similarity for ${queued} models; processing continues in the background.`,
          error: null,
        };
      }
      await Promise.all([this.refreshSelectedModelSimilarityRuns(), this.refreshProcessingsRegistry()]);
    } finally {
      this.similarityBulkPending = false;
      this.changeDetector.markForCheck();
    }
  }

  public async rerunSelectedSceneGeometryReuse(): Promise<void> {
    if (this.selectedSceneId === null || this.sceneReusePending || this.uploadInProgress) {
      return;
    }

    const sceneId: string = this.selectedSceneId;
    this.sceneReusePending = true;
    this.changeDetector.markForCheck();
    try {
      const response: SceneGeometryReuseResponse = await this.ingestAdapter.rerunSceneGeometryReuse(
        this.ingestApiBaseUrl,
        sceneId
      );
      const tasks: SimilarityRerunTask[] = response.processingIds.map((processingId: string) => ({
        modelId: `scene:${sceneId}`,
        processingId,
        status: 'Queued',
      }));
      this.sceneReuseResults = [...tasks, ...this.sceneReuseResults];
      if (tasks.length === 0) {
        this.ingestState = {
          ...this.ingestState,
          message: 'No eligible non-photogrammetry models require scene reuse.',
          error: null,
        };
      } else {
        tasks.forEach((task: SimilarityRerunTask) => void this.trackSimilarityRerun(task, task.processingId!));
      }
      await this.refreshProcessingsRegistry();
    } catch (error: unknown) {
      this.setIngestError(error instanceof Error ? error.message : 'Failed to queue scene geometry reuse.');
    } finally {
      this.sceneReusePending = false;
      this.changeDetector.markForCheck();
    }
  }

  private async trackSimilarityRerun(task: SimilarityRerunTask, processingId: string): Promise<void> {
    try {
      while (!this.similarityRerunTrackingCanceled) {
        const processing: HypersceneProcessingView = await this.ingestAdapter.getProcessing(
          this.ingestApiBaseUrl,
          processingId
        );
        if (this.similarityRerunTrackingCanceled) return;
        task.status = processing.status;
        this.changeDetector.markForCheck();
        if (['Completed', 'Failed', 'Canceled'].includes(processing.status)) {
          if (processing.status !== 'Completed') {
            task.error = processing.errorInfo ?? `Processing ${processing.status.toLowerCase()}.`;
          }
          if (this.selectedModelId === task.modelId) {
            await this.refreshSelectedModelSimilarityRuns();
          }
          if (processing.status === 'Completed') {
            await Promise.all([this.refreshScenesRegistry(), this.refreshProcessingsRegistry()]);
          }
          return;
        }
        await this.delay(SIMILARITY_POLL_INTERVAL_MS);
      }
    } catch (error: unknown) {
      if (!this.similarityRerunTrackingCanceled) {
        task.status = 'Error';
        task.error = error instanceof Error ? error.message : 'Failed to monitor similarity processing.';
      }
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public async renameSelectedScene(): Promise<void> {
    if (this.selectedSceneId === null) {
      this.setIngestError('Select scene to rename.');
      return;
    }

    const sceneName: string = this.selectedSceneRenameDraft.trim();
    if (sceneName.length === 0) {
      this.setIngestError('Provide scene name before renaming.');
      return;
    }

    const scene: HypersceneSceneView | undefined = this.scenesRegistry.find(
      (sceneView: HypersceneSceneView) => sceneView.id === this.selectedSceneId
    );

    try {
      const metadataPayload: unknown[] = this.buildRenamedMetadataPayload(scene?.metadata, 'sceneName', sceneName);
      await this.ingestAdapter.putSceneMetadata(this.ingestApiBaseUrl, this.selectedSceneId, metadataPayload);
      await this.refreshScenesRegistry();
      this.syncSelectedSceneRenameDraft();
      this.ingestState = {
        ...this.ingestState,
        message: `Scene ${this.selectedSceneId} renamed to ${sceneName}.`,
        error: null,
      };
      this.artifactRenameTarget = null;
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to rename scene.';
      this.setIngestError(errorMessage);
    }
  }

  public async clearProcessingsRegistry(): Promise<void> {
    const processingIds: string[] = this.getClearableProcessingIds();
    if (processingIds.length === 0) {
      return;
    }

    try {
      await this.ingestAdapter.deleteProcessings(this.ingestApiBaseUrl, processingIds);
      if (this.selectedProcessingId !== null && processingIds.includes(this.selectedProcessingId)) {
        this.selectedProcessingId = null;
        this.selectedProcessingDetails = null;
      }
      if (this.trackedProcessingId !== null && processingIds.includes(this.trackedProcessingId)) {
        this.trackedProcessingId = null;
        this.trackedProcessingStatus = 'n/a';
        this.trackedProcessingType = 'n/a';
        this.trackedProcessingLastCheckedAt = 'n/a';
        this.trackedProcessingHistory = [];
      }
      await this.refreshProcessingsRegistry();
      this.ingestState = {
        ...this.ingestState,
        processingId:
          this.ingestState.processingId !== null && processingIds.includes(this.ingestState.processingId)
            ? null
            : this.ingestState.processingId,
        processingStatus:
          this.ingestState.processingId !== null && processingIds.includes(this.ingestState.processingId)
            ? null
            : this.ingestState.processingStatus,
        message: `Terminal processings cleared: ${processingIds.length}.`,
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to clear processings.';
      this.setIngestError(errorMessage);
    }
  }

  public getClearableProcessingsCount(): number {
    return this.getClearableProcessingIds().length;
  }

  public selectProcessing(processing: HypersceneProcessingView): void {
    this.selectedProcessingId = processing.id;
    this.selectedProcessingDetails = processing;
  }

  public getProcessingStartedAt(processing: HypersceneProcessingView): number | null {
    const started: string | null | undefined = processing.started;
    if (started === undefined || started === null || started.length === 0 || started.startsWith('0001-')) {
      return null;
    }
    const timestamp: number = Date.parse(started);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  public getProcessingFileName(processing: HypersceneProcessingView): string {
    const maximumVisibleFileNames: number = 5;
    const names: string[] = (processing.filePaths ?? []).flatMap((file: unknown) => {
      if (typeof file !== 'object' || file === null) {
        return [];
      }
      const fileName: unknown = (file as { fileName?: unknown }).fileName;
      return typeof fileName === 'string' && fileName.trim().length > 0 ? [fileName] : [];
    });
    if (names.length === 0) {
      return '-';
    }

    const visibleNames: string = names.slice(0, maximumVisibleFileNames).join(', ');
    const remainingCount: number = names.length - maximumVisibleFileNames;
    return remainingCount > 0 ? `${visibleNames} (+${remainingCount} more)` : visibleNames;
  }

  public getProcessingTypeLabel(processing: HypersceneProcessingView | null): string {
    if (processing === null || processing.$type === undefined) {
      return 'unknown';
    }

    const rawType: string = processing.$type;
    const simpleName: string = rawType.includes('.') ? rawType.split('.').pop() ?? rawType : rawType;
    const typeName: string = simpleName.replace(/ProcessingView$/i, '');
    return typeName === 'HlodTextureCompression' ? 'KTX2 textures' : typeName;
  }

  public getProcessingDuration(processing: HypersceneProcessingView): string {
    return formatProcessingDuration(getProcessingDurationMs(processing, Date.now()));
  }

  public getProcessingNodeProgress(processing: HypersceneProcessingView | null): string | null {
    if (processing?.totalNodes === undefined) {
      return null;
    }
    const done: number = (processing.compressedNodes ?? 0) + (processing.unchangedNodes ?? 0);
    return `${done} of ${processing.totalNodes} nodes (${processing.unchangedNodes ?? 0} already KTX2)`;
  }

  public getProcessingMetadataSummary(processing: HypersceneProcessingView | null): string {
    if (processing === null || processing.metadata === undefined || processing.metadata.length === 0) {
      return 'n/a';
    }

    return processing.metadata.join(', ');
  }

  public openSceneModelAction(action: 'create' | 'attach' | 'instance'): void {
    this.sceneModelAction = action;
    this.sceneActionModelId = null;
    this.sceneActionModelIds = [];
    this.sceneActionSceneId = action === 'create' ? null : this.selectedSceneId;
    this.sceneActionNameDraft = '';
    this.sceneInstanceDrafts = action === 'instance' ? [this.createSceneModelInstanceDraft()] : [];
  }

  public closeSceneModelAction(): void {
    this.sceneModelAction = null;
    this.sceneActionModelId = null;
    this.sceneActionModelIds = [];
    this.sceneActionSceneId = null;
    this.sceneActionNameDraft = '';
    this.sceneInstanceDrafts = [];
  }

  public setSceneActionModel(modelId: string | null): void {
    this.sceneActionModelId = modelId;
    this.sceneActionNameDraft =
      this.sceneModelAction === 'create' && this.sceneActionModelId !== null
        ? this.resolveSelectedModelFileName(this.sceneActionModelId) ?? ''
        : '';
  }

  public onSceneActionAttachModelChanged(modelId: string, event: Event): void {
    if (!(event.target instanceof HTMLInputElement) || this.sceneModelActionPending) {
      return;
    }
    this.sceneActionModelIds = event.target.checked
      ? [...new Set([...this.sceneActionModelIds, modelId])]
      : this.sceneActionModelIds.filter((selectedId: string) => selectedId !== modelId);
  }

  public onSceneActionNameDraftChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.sceneActionNameDraft = event.target.value;
    }
  }

  public addSceneModelInstanceDraft(): void {
    this.sceneInstanceDrafts = [...this.sceneInstanceDrafts, this.createSceneModelInstanceDraft()];
  }

  public removeSceneModelInstanceDraft(index: number): void {
    if (index < 0 || index >= this.sceneInstanceDrafts.length) {
      return;
    }

    this.sceneInstanceDrafts = this.sceneInstanceDrafts.filter(
      (_draft: HypersceneSceneModelInstanceDraft, draftIndex: number) => draftIndex !== index
    );
  }

  public setSceneInstanceModel(index: number, modelId: string | null): void {
    const draft: HypersceneSceneModelInstanceDraft | undefined = this.sceneInstanceDrafts[index];
    if (draft === undefined) {
      return;
    }

    draft.modelId = modelId;
    if (modelId !== null && draft.instanceName.trim().length === 0) {
      const modelName: string = this.resolveSelectedModelFileName(modelId) ?? 'model';
      draft.instanceName = `${modelName} instance`;
    }
  }

  public onSceneInstanceNameChanged(index: number, event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      const draft: HypersceneSceneModelInstanceDraft | undefined = this.sceneInstanceDrafts[index];
      if (draft !== undefined) {
        draft.instanceName = event.target.value;
      }
    }
  }

  public onSceneInstanceTransformChanged(
    index: number,
    group: 'translation' | 'rotation' | 'scale',
    axis: 'x' | 'y' | 'z',
    event: Event
  ): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const value: number = Number(event.target.value);
    if (!Number.isFinite(value)) {
      return;
    }

    const instanceDraft: HypersceneSceneModelInstanceDraft | undefined = this.sceneInstanceDrafts[index];
    if (instanceDraft === undefined) {
      return;
    }

    const draft: HypersceneTransformVectorView =
      group === 'translation'
        ? instanceDraft.translation
        : group === 'rotation'
        ? instanceDraft.rotation
        : instanceDraft.scale;
    draft[axis] = value;
  }

  public hasInvalidSceneModelInstanceDrafts(): boolean {
    return (
      this.sceneInstanceDrafts.length === 0 ||
      this.sceneInstanceDrafts.some(
        (draft: HypersceneSceneModelInstanceDraft) => draft.modelId === null || draft.instanceName.trim().length === 0
      )
    );
  }

  private createSceneModelInstanceDraft(
    modelId: string | null = null,
    instanceName: string = ''
  ): HypersceneSceneModelInstanceDraft {
    return {
      modelId,
      instanceName,
      translation: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
    };
  }

  private createSceneTransformDraft(): HypersceneSceneTransformDraft {
    return {
      translation: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
    };
  }

  public async confirmSceneModelAction(): Promise<void> {
    if (
      this.sceneModelActionPending ||
      this.sceneModelAction === null ||
      (this.sceneModelAction === 'create' && this.sceneActionModelId === null) ||
      (this.sceneModelAction === 'attach' && this.sceneActionModelIds.length === 0)
    ) {
      return;
    }

    this.sceneModelActionPending = true;
    try {
      if (this.sceneModelAction === 'create') {
        await this.createSceneFromSelectedModel();
      } else if (this.sceneModelAction === 'instance') {
        await this.createSceneModelInstancesInSelectedScene();
      } else {
        await this.attachSelectedModelToSelectedScene();
      }
      if (this.ingestState.error === null) {
        this.closeSceneModelAction();
      }
    } finally {
      this.sceneModelActionPending = false;
      this.changeDetector.markForCheck();
    }
  }

  public async createSceneFromSelectedModel(): Promise<void> {
    const modelId: string | null = this.sceneActionModelId;
    if (modelId === null) {
      this.setIngestError('Active model is not set. Run ingest or load scene first.');
      return;
    }

    try {
      const fileName: string | null = this.resolveSelectedModelFileName(modelId);
      const sceneActionName: string = this.sceneActionNameDraft.trim();
      const modelFileName: string = fileName?.trim() ?? '';
      const sceneName: string =
        sceneActionName.length > 0 ? sceneActionName : modelFileName.length > 0 ? modelFileName : 'scene';
      const sceneMetadata: unknown[] = this.buildSceneMetadataPayload(fileName, modelId, sceneName);
      const scene: HypersceneSceneView = await this.ingestAdapter.createScene(this.ingestApiBaseUrl, sceneMetadata);
      const rootNode: HypersceneNodeView = await this.ingestAdapter.getRootNode(this.ingestApiBaseUrl, modelId);
      await this.ingestAdapter.putNodeTreeToScene(this.ingestApiBaseUrl, scene.id, rootNode.id);
      this.selectedSceneId = scene.id;
      this.ingestState = {
        ...this.ingestState,
        sceneId: scene.id,
        modelId,
        rootNodeId: rootNode.id,
        message: `Scene ${scene.id} created from model ${modelId}.`,
        error: null,
      };
      await this.refreshScenesRegistry();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to create scene from model.';
      this.setIngestError(errorMessage);
    }
  }

  public async attachSelectedModelToSelectedScene(): Promise<void> {
    const sceneId: string | null = this.sceneActionSceneId;
    if (sceneId === null) {
      this.setIngestError('Current scene is not set. Load scene first.');
      return;
    }

    const modelIds: string[] = [...this.sceneActionModelIds];
    if (modelIds.length === 0) {
      this.setIngestError('Select at least one model to attach to current scene.');
      return;
    }

    let attachedCount: number = 0;
    let lastRootNodeId: string | null = null;
    try {
      for (const modelId of modelIds) {
        const rootNode: HypersceneNodeView = await this.ingestAdapter.getRootNode(this.ingestApiBaseUrl, modelId);
        await this.ingestAdapter.putNodeTreeToScene(this.ingestApiBaseUrl, sceneId, rootNode.id);
        lastRootNodeId = rootNode.id;
        attachedCount++;
      }
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to attach model to scene.';
      if (attachedCount > 0) {
        this.sceneActionModelIds = modelIds.slice(attachedCount);
      }
      this.setIngestError(
        attachedCount === 0
          ? errorMessage
          : `${errorMessage} Attached ${attachedCount} of ${modelIds.length} models before the failure.`
      );
      if (attachedCount > 0) {
        try {
          await this.refreshArtifactSceneRoots(sceneId);
          await this.refreshSelectedModelScenes();
        } catch (refreshError: unknown) {
          const refreshMessage: string =
            refreshError instanceof Error ? refreshError.message : 'Failed to refresh scene after partial attachment.';
          this.setIngestError(
            `${errorMessage} Attached ${attachedCount} of ${modelIds.length} models before the failure. ${refreshMessage}`
          );
        }
      }
      return;
    }

    this.ingestState = {
      ...this.ingestState,
      sceneId,
      modelId: modelIds[modelIds.length - 1],
      rootNodeId: lastRootNodeId,
      message: `Attached ${attachedCount} model${attachedCount === 1 ? '' : 's'} to scene ${sceneId}.`,
      error: null,
    };
    try {
      await this.refreshArtifactSceneRoots(sceneId);
      await this.refreshSelectedModelScenes();
    } catch (error: unknown) {
      const refreshMessage: string =
        error instanceof Error ? error.message : 'Failed to refresh scene after attachment.';
      this.setIngestError(
        `Attached ${attachedCount} models to scene ${sceneId}, but failed to refresh: ${refreshMessage}`
      );
      this.sceneActionModelIds = [];
    }
  }

  public async createSceneModelInstancesInSelectedScene(): Promise<void> {
    const sceneId: string | null = this.sceneActionSceneId;
    if (sceneId === null) {
      this.setIngestError('Select scene for the model instances.');
      return;
    }

    if (this.hasInvalidSceneModelInstanceDrafts()) {
      this.setIngestError('Select a model and enter a name for every model instance.');
      return;
    }

    let createdCount: number = 0;
    try {
      const sourceRoots: Map<string, HypersceneNodeView> = new Map<string, HypersceneNodeView>();
      let lastInstanceRootNode: HypersceneNodeView | null = null;

      for (const draft of this.sceneInstanceDrafts) {
        if (draft.modelId === null) {
          throw new Error('A model must be selected for every model instance.');
        }

        const modelId: string = draft.modelId;
        let sourceRootNode: HypersceneNodeView | undefined = sourceRoots.get(modelId);
        if (sourceRootNode === undefined) {
          sourceRootNode = await this.ingestAdapter.getRootNode(this.ingestApiBaseUrl, modelId);
          sourceRoots.set(modelId, sourceRootNode);
        }

        lastInstanceRootNode = await this.ingestAdapter.createSceneModelInstance(this.ingestApiBaseUrl, sceneId, {
          sourceRootNodeId: sourceRootNode.id,
          instanceName: draft.instanceName.trim(),
          transformInfo: {
            translation: draft.translation,
            rotation: draft.rotation,
            scale: draft.scale,
          },
        });
        createdCount += 1;
      }

      this.selectedSceneNodeId = lastInstanceRootNode?.id ?? null;
      this.selectedSceneNodeTitle = lastInstanceRootNode?.title ?? null;
      this.ingestState = {
        ...this.ingestState,
        sceneId,
        modelId: this.sceneInstanceDrafts[this.sceneInstanceDrafts.length - 1]?.modelId ?? this.ingestState.modelId,
        rootNodeId: lastInstanceRootNode?.id ?? this.ingestState.rootNodeId,
        message: `Created ${createdCount} model instance${createdCount === 1 ? '' : 's'} in scene ${sceneId}.`,
        error: null,
      };
      await this.refreshArtifactSceneRoots(sceneId);
      await this.refreshSelectedModelScenes();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to create model instances.';
      const progressMessage: string =
        createdCount === 0 ? errorMessage : `${errorMessage} Created ${createdCount} instance(s) before the failure.`;
      this.setIngestError(progressMessage);
      if (createdCount > 0) {
        await this.refreshSelectedModelScenes();
      }
    }
  }

  private async refreshArtifactSceneRoots(sceneId: string | null): Promise<void> {
    try {
      if (sceneId === null) {
        this.sceneTreeRoots = [];
        this.selectedSceneNodeId = null;
        return;
      }

      const sceneRootNodes: HypersceneNodeSmallView[] = await this.ingestAdapter.getSceneRootNodes(
        this.ingestApiBaseUrl,
        sceneId
      );
      this.sceneTreeRoots = this.filterSceneContentRoots(sceneRootNodes).map((node: HypersceneNodeSmallView) =>
        this.makeSceneTreeNode(node)
      );
      if (
        this.selectedSceneNodeId !== null &&
        !sceneRootNodes.some((node: HypersceneNodeSmallView) => node.id === this.selectedSceneNodeId)
      ) {
        this.selectedSceneNodeId = null;
        this.clearSelectedSceneModelInstance();
      }
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load scene model instances.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public async deleteSelectedSceneFromRegistry(): Promise<void> {
    if (this.selectedSceneId === null) {
      return;
    }

    try {
      await this.ingestAdapter.deleteScene(this.ingestApiBaseUrl, this.selectedSceneId);
      const removedSceneId: string = this.selectedSceneId;
      this.selectedSceneId = null;
      this.sceneContextAction = null;
      this.artifactRenameTarget = null;
      await this.refreshScenesRegistry();
      if (this.ingestState.sceneId === removedSceneId) {
        this.sceneTreeRoots = [];
        this.selectedSceneNodeId = null;
      }
      this.ingestState = {
        ...this.ingestState,
        message: `Scene ${removedSceneId} deleted.`,
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      if (this.isDecodeCanceledError(error)) {
        return;
      }

      const errorMessage: string = error instanceof Error ? error.message : 'Failed to delete selected scene.';
      this.setIngestError(errorMessage);
    }
  }

  public async deleteSceneFromRegistry(sceneId: string): Promise<void> {
    this.selectedSceneId = sceneId;
    this.syncSelectedSceneRenameDraft();
    await this.deleteSelectedSceneFromRegistry();
  }

  public async refreshSelectedProcessingStatus(): Promise<void> {
    if (this.selectedProcessingId === null) {
      return;
    }

    try {
      const processing: HypersceneProcessingView = await this.ingestAdapter.getProcessing(
        this.ingestApiBaseUrl,
        this.selectedProcessingId
      );
      this.selectedProcessingDetails = processing;
      if (this.trackedProcessingId !== null && processing.id === this.trackedProcessingId) {
        this.updateTrackedConversionStatus(processing);
      }
      this.ingestState = {
        ...this.ingestState,
        processingId: processing.id,
        processingStatus: processing.status,
        modelId: processing.modelId ?? this.ingestState.modelId,
        message: `Processing ${processing.id} status: ${processing.status}.`,
        error: null,
      };
      await this.refreshProcessingsRegistry();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to refresh processing status.';
      this.setIngestError(errorMessage);
    }
  }

  public onProcessingsAutoRefreshChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.processingsAutoRefreshEnabled = event.target.checked;
    this.updateProcessingsAutoRefreshLoop();
    this.changeDetector.markForCheck();
  }

  public updateProcessingsAutoRefreshLoop(): void {
    if (this.processingsAutoRefreshTimer !== null) {
      window.clearInterval(this.processingsAutoRefreshTimer);
      this.processingsAutoRefreshTimer = null;
    }

    if (!this.processingsAutoRefreshEnabled) {
      return;
    }

    this.processingsAutoRefreshTimer = window.setInterval(() => {
      void this.refreshProcessingsRegistry();
    }, this.processingsPollingMs);
  }

  public getRegistryMetadataFilter(): unknown[] {
    if (!this.registryUseTagFilter || this.ingestTag.trim().length === 0) {
      return [];
    }

    return [
      {
        source: this.ingestTag.trim(),
      },
    ];
  }

  public onUploadFileChanged(event: Event): void {
    if (this.uploadInProgress) {
      return;
    }
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const files: File[] = Array.from(event.target.files ?? []);
    const file: File | null = files[0] ?? null;
    this.selectedUploadFile = file;
    this.selectedUploadFiles = files;

    if (file !== null) {
      this.ingestParserType = this.inferParserType(file.name);
    }
    const message: string =
      file === null
        ? 'Select a file and run Upload -> Parse.'
        : files.length === 1
        ? `Ready to upload ${file.name}`
        : `Ready to upload ${files.length} files`;

    this.ingestState = {
      ...this.ingestState,
      fileName: file?.name ?? null,
      parserType: this.ingestParserType,
      message,
      error: null,
    };
    this.changeDetector.markForCheck();
  }

  public async runIngestFlow(): Promise<void> {
    if (this.uploadInProgress) {
      return;
    }
    this.uploadInProgress = true;
    try {
      if (this.selectedUploadFiles.length > 1) {
        await this.executeArtifactsIngestBatch();
      } else {
        await this.executeIngestFlow();
      }
    } finally {
      this.uploadInProgress = false;
      this.changeDetector.markForCheck();
    }
  }

  private async executeIngestFlow(): Promise<void> {
    if (this.selectedUploadFile === null) {
      this.setIngestError('Select a source file first.');
      return;
    }

    this.isPollingCanceled = false;
    this.ingestState = {
      ...this.ingestState,
      phase: 'uploading',
      fileName: this.selectedUploadFile.name,
      parserType: this.ingestParserType,
      documentId: null,
      processingId: null,
      modelId: null,
      rootNodeId: null,
      sceneId: null,
      sceneRootNodesCount: 0,
      geometryIdsCount: 0,
      meshGeometryIdsCount: 0,
      lineGeometryIdsCount: 0,
      pointCloudGeometryIdsCount: 0,
      renderedMeshObjectsCount: 0,
      renderedLineObjectsCount: 0,
      renderedPointCloudObjectsCount: 0,
      collidedPointsCount: 0,
      meshesBinaryBytes: 0,
      linesBinaryBytes: 0,
      pointCloudsBinaryBytes: 0,
      processingStatus: null,
      error: null,
      message: 'Uploading source file...',
    };
    this.changeDetector.markForCheck();

    try {
      const ingestRequestId: string = getUuid();
      const metadataPayload: {
        source: string;
        fileName: string;
        ingestRequestId: string;
      } = {
        source: this.ingestTag,
        fileName: this.selectedUploadFile.name,
        ingestRequestId,
      };

      const document: HypersceneDocumentView = await this.ingestAdapter.uploadDocument(
        this.ingestApiBaseUrl,
        this.selectedUploadFile
      );

      this.ingestState = {
        ...this.ingestState,
        phase: 'parsing',
        documentId: document.id,
        message: 'Starting parse processing...',
      };
      this.changeDetector.markForCheck();

      const parseRequest: Record<string, unknown> = {
        modelName: this.selectedUploadFile.name,
        parentVersionId: null,
        parserType: this.ingestParserType,
        filePathInfos: [
          {
            filePath: document.filePath,
            fileBucket: document.bucket,
            fileName: this.selectedUploadFile.name,
          },
        ],
        metadataInfos: [metadataPayload],
      };
      const processingId: string = await this.ingestAdapter.parseDocument(this.ingestApiBaseUrl, parseRequest);

      this.ingestState = {
        ...this.ingestState,
        phase: 'polling',
        processingId,
        message: 'Parse started. Polling processing status...',
      };
      this.changeDetector.markForCheck();
      await this.refreshProcessingsRegistry();

      const processing: HypersceneProcessingView = await this.waitProcessingCompleted(processingId);
      if (this.isPollingCanceled) {
        this.ingestState = {
          ...this.ingestState,
          phase: 'canceled',
          message: 'Polling canceled.',
        };
        this.changeDetector.markForCheck();
        return;
      }

      if (processing.status !== 'Completed') {
        this.setIngestError(`Processing finished with status: ${processing.status}`);
        return;
      }

      this.ingestState = {
        ...this.ingestState,
        phase: 'resolving',
        processingStatus: processing.status,
        modelId: processing.modelId ?? null,
        message: 'Resolving model and root node...',
      };
      this.changeDetector.markForCheck();

      const modelId: string = await this.resolveModelId(processing, [metadataPayload]);
      this.selectedModelId = modelId;
      this.ingestState = {
        ...this.ingestState,
        modelId,
        phase: 'ready',
        message: `Model ${this.selectedUploadFile.name} uploaded.`,
      };
      await this.refreshModelsRegistry();
      await this.refreshProcessingsRegistry();
    } catch (error: unknown) {
      if (this.isDecodeCanceledError(error)) {
        this.ingestState = {
          ...this.ingestState,
          message: 'Previous decode tasks were canceled due to scene context switch.',
          error: null,
        };
        this.changeDetector.markForCheck();
        return;
      }

      const errorMessage: string = error instanceof Error ? error.message : 'Unknown ingest error';
      this.setIngestError(errorMessage);
    }
  }

  private async executeArtifactsIngestBatch(): Promise<void> {
    const files: readonly File[] = this.selectedUploadFiles;
    if (files.length === 0) {
      this.setIngestError('Select source files first.');
      return;
    }

    this.ingestState = {
      ...this.ingestState,
      phase: 'uploading',
      fileName: null,
      parserType: this.ingestParserType,
      documentId: null,
      processingId: null,
      modelId: null,
      error: null,
      message: `Uploading ${files.length} source files...`,
    };
    this.changeDetector.markForCheck();

    const results: PromiseSettledResult<string>[] = await Promise.allSettled(
      files.map(async (file: File): Promise<string> => {
        const ingestRequestId: string = getUuid();
        const metadataPayload: { source: string; fileName: string; ingestRequestId: string } = {
          source: this.ingestTag,
          fileName: file.name,
          ingestRequestId,
        };
        const document: HypersceneDocumentView = await this.ingestAdapter.uploadDocument(this.ingestApiBaseUrl, file);
        return this.ingestAdapter.parseDocument(this.ingestApiBaseUrl, {
          modelName: file.name,
          parentVersionId: null,
          parserType: this.ingestParserType,
          filePathInfos: [
            {
              filePath: document.filePath,
              fileBucket: document.bucket,
              fileName: file.name,
            },
          ],
          metadataInfos: [metadataPayload],
        });
      })
    );
    const startedCount: number = results.filter(
      (result: PromiseSettledResult<string>) => result.status === 'fulfilled'
    ).length;
    const failedCount: number = files.length - startedCount;
    await this.refreshProcessingsRegistry();
    await this.refreshModelsRegistry();

    if (startedCount === 0) {
      this.setIngestError('Could not start parsing the selected files.');
      return;
    }

    this.ingestState = {
      ...this.ingestState,
      phase: 'polling',
      message: `Started parsing ${startedCount} ${startedCount === 1 ? 'model' : 'models'}.`,
      error:
        failedCount === 0 ? null : `Could not start parsing ${failedCount} ${failedCount === 1 ? 'file' : 'files'}.`,
    };
    this.changeDetector.markForCheck();
  }

  public isSelectedSceneRootNode(): boolean {
    if (this.selectedSceneNodeId === null) {
      return false;
    }

    return this.sceneTreeRoots.some((rootNode: HypersceneSceneTreeNode) => rootNode.id === this.selectedSceneNodeId);
  }

  public async removeSelectedRootNodeFromScene(): Promise<void> {
    const sceneId: string | null = this.selectedSceneId;
    if (sceneId === null) {
      this.setIngestError('Scene is not initialized yet. Run ingest first.');
      return;
    }

    const selectedNodeId: string | null = this.selectedSceneNodeId;
    if (selectedNodeId === null) {
      this.setIngestError('Select root node in scene tree first.');
      return;
    }

    if (!this.isSelectedSceneRootNode()) {
      this.setIngestError('Only root nodes can be removed from scene.');
      return;
    }

    try {
      const instance: HypersceneSceneModelInstanceView | null = await this.ingestAdapter.getSceneModelInstance(
        this.ingestApiBaseUrl,
        sceneId,
        selectedNodeId
      );
      const force: boolean = instance !== null && this.isMatchingSceneModelInstance(instance, sceneId, selectedNodeId);
      await this.ingestAdapter.removeNodesTreesFromScene(this.ingestApiBaseUrl, sceneId, [selectedNodeId], force);
      this.selectedSceneNodeId = null;
      this.clearSelectedSceneModelInstance();

      await this.refreshArtifactSceneRoots(sceneId);
      await this.refreshSelectedModelScenes();

      this.ingestState = {
        ...this.ingestState,
        message: `Root node ${selectedNodeId} removed from scene ${sceneId}.`,
        error: null,
      };
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to remove root node from scene.';
      this.setIngestError(errorMessage);
      return;
    }

    this.changeDetector.markForCheck();
  }

  public getModelDisplayName(model: HypersceneModelView): string {
    const idPart: string = model.id.slice(0, 6);
    const modelName: string = this.tryExtractModelDisplayName(model.metadata) ?? '-';
    return `${idPart} | ${modelName}`;
  }

  public getModelDisplayDate(model: HypersceneModelView): string {
    return this.resolveModelCreatedAt(model);
  }

  public getSceneDisplayName(scene: HypersceneSceneView): string {
    const sceneName: string | null = this.tryExtractMetadataString(scene.metadata, 'sceneName');
    const fileName: string | null = this.tryExtractMetadataString(scene.metadata, 'fileName');
    return sceneName !== null && fileName !== null && sceneName.startsWith(`${fileName} | file:`)
      ? this.normalizeSceneDisplayName(fileName)
      : this.normalizeSceneDisplayName(sceneName ?? fileName ?? 'Unnamed scene');
  }

  public getSceneDisplayDate(scene: HypersceneSceneView): string {
    return this.formatModelDate(this.resolveSceneCreatedAt(scene));
  }

  private resolveSceneCreatedAt(scene: HypersceneSceneView): string | null {
    return scene.createdAt ?? this.tryExtractMetadataString(scene.metadata, 'createdAt');
  }

  private filterArtifactRegistry<T>(items: T[], query: string, getLabel: (item: T) => string): T[] {
    const normalizedQuery: string = query.trim().toLocaleLowerCase();
    if (normalizedQuery.length === 0) {
      return items;
    }

    return items.filter((item: T) => getLabel(item).toLocaleLowerCase().includes(normalizedQuery));
  }

  private sortModelsByCreatedAt(models: HypersceneModelView[]): HypersceneModelView[] {
    return this.sortByCreationDate(models, (model: HypersceneModelView) => this.resolveModelCreationTimestamp(model));
  }

  private sortScenesByCreatedAt(scenes: HypersceneSceneView[]): HypersceneSceneView[] {
    return this.sortByCreationDate(scenes, (scene: HypersceneSceneView) => this.resolveSceneCreationTimestamp(scene));
  }

  private sortByCreationDate<T>(items: T[], resolveTimestamp: (item: T) => number | null): T[] {
    interface SortEntry {
      item: T;
      index: number;
      timestamp: number | null;
    }

    return items
      .map((item: T, index: number) => ({ item, index, timestamp: resolveTimestamp(item) }))
      .sort((left: SortEntry, right: SortEntry) => {
        if (left.timestamp === null && right.timestamp !== null) {
          return 1;
        }
        if (left.timestamp !== null && right.timestamp === null) {
          return -1;
        }
        if (left.timestamp !== null && right.timestamp !== null && left.timestamp !== right.timestamp) {
          return right.timestamp - left.timestamp;
        }
        return left.index - right.index;
      })
      .map(({ item }: SortEntry) => item);
  }

  private resolveModelCreationTimestamp(model: HypersceneModelView): number | null {
    const directCreatedAt: string | null = model.createdAt ?? model.uploadedAt ?? null;
    const metadataCreatedAt: string | null = this.tryExtractModelCreatedAt(model.metadata);
    const processingDates: string[] = this.processingsRegistry
      .filter((processing: HypersceneProcessingView) => processing.modelId === model.id)
      .flatMap((processing: HypersceneProcessingView) => [processing.completed, processing.started])
      .filter((value: string | null | undefined): value is string => value !== undefined && value !== null);

    for (const value of [directCreatedAt, metadataCreatedAt, ...processingDates]) {
      if (value === null || value.trim().length === 0) {
        continue;
      }
      const timestamp: number = Date.parse(value);
      if (!Number.isNaN(timestamp)) {
        return timestamp;
      }
    }

    return null;
  }

  private resolveSceneCreationTimestamp(scene: HypersceneSceneView): number | null {
    const createdAt: string | null = scene.createdAt ?? this.tryExtractMetadataString(scene.metadata, 'createdAt');
    if (createdAt === null || createdAt.trim().length === 0) {
      return null;
    }

    const timestamp: number = Date.parse(createdAt);
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  private normalizeSceneDisplayName(name: string): string {
    return name.trim();
  }

  private resolveDefaultIngestApiBaseUrl(): string {
    const runtimeConfig: HypersceneRuntimeConfig | undefined =
      // eslint-disable-next-line @typescript-eslint/naming-convention
      (globalThis as { __HYPERSCENE_RUNTIME_CONFIG__?: HypersceneRuntimeConfig }).__HYPERSCENE_RUNTIME_CONFIG__;
    const runtimeApiBaseUrl: string | undefined = runtimeConfig?.apiBaseUrl?.trim();
    if (runtimeApiBaseUrl !== undefined && runtimeApiBaseUrl.length > 0) {
      return runtimeApiBaseUrl;
    }

    const locationRef: Location | undefined = globalThis.location;
    if (locationRef === undefined) {
      return 'http://localhost:5000';
    }

    const hostName: string = locationRef.hostname.toLowerCase();
    const isLocalHost: boolean = hostName === 'localhost' || hostName === '127.0.0.1' || hostName === '::1';
    if (isLocalHost) {
      return 'http://localhost:5000';
    }

    return `${locationRef.protocol}//api.${locationRef.hostname}`;
  }

  private async waitProcessingCompleted(processingId: string): Promise<HypersceneProcessingView> {
    for (let attempt: number = 0; attempt < PROCESSING_POLL_ATTEMPTS; attempt++) {
      if (this.isPollingCanceled) {
        break;
      }

      const processing: HypersceneProcessingView = await this.ingestAdapter.getProcessing(
        this.ingestApiBaseUrl,
        processingId
      );

      this.ingestState = {
        ...this.ingestState,
        processingStatus: processing.status,
        modelId: processing.modelId ?? this.ingestState.modelId,
        message: `Processing status: ${processing.status}`,
      };
      this.changeDetector.markForCheck();

      if (processing.status === 'Completed' || processing.status === 'Failed' || processing.status === 'Canceled') {
        return processing;
      }

      await this.delay(PROCESSING_POLL_INTERVAL_MS);
    }

    throw new Error('Processing polling timed out or was canceled.');
  }

  private async resolveModelId(processing: HypersceneProcessingView, metadataInfos: unknown[]): Promise<string> {
    if (processing.modelId !== undefined && processing.modelId !== null && processing.modelId.length > 0) {
      return processing.modelId;
    }

    for (let attempt: number = 0; attempt < 10; attempt++) {
      const actualProcessing: HypersceneProcessingView = await this.ingestAdapter.getProcessing(
        this.ingestApiBaseUrl,
        processing.id
      );

      if (
        actualProcessing.modelId !== undefined &&
        actualProcessing.modelId !== null &&
        actualProcessing.modelId.length > 0
      ) {
        return actualProcessing.modelId;
      }

      await this.delay(500);
    }

    const models: HypersceneModelView[] = await this.ingestAdapter.filterModels(this.ingestApiBaseUrl, metadataInfos);

    if (models.length === 0) {
      throw new Error('No models found by parse metadata.');
    }

    if (models.length > 1) {
      throw new Error('Multiple models matched parse metadata. Please retry ingest with unique metadata.');
    }

    return models[0].id;
  }

  private syncTrackedConversionStatusFromRegistry(processings: HypersceneProcessingView[]): void {
    if (this.trackedProcessingId === null) {
      return;
    }

    const trackedProcessing: HypersceneProcessingView | undefined = processings.find(
      (processing: HypersceneProcessingView) => processing.id === this.trackedProcessingId
    );
    if (trackedProcessing === undefined) {
      return;
    }

    this.updateTrackedConversionStatus(trackedProcessing);
  }

  private updateTrackedConversionStatus(processing: HypersceneProcessingView): void {
    const nextStatus: string = processing.status;
    const previousStatus: string = this.trackedProcessingStatus;
    const checkedAt: string = new Date().toLocaleTimeString();

    this.trackedProcessingStatus = nextStatus;
    this.trackedProcessingType = this.getProcessingTypeLabel(processing);
    this.trackedProcessingLastCheckedAt = checkedAt;

    if (previousStatus !== nextStatus) {
      this.trackedProcessingHistory = [`${checkedAt} -> ${nextStatus}`, ...this.trackedProcessingHistory].slice(0, 20);
    }
  }

  private getClearableProcessingIds(): string[] {
    return this.processingsRegistry
      .filter((processing: HypersceneProcessingView) => this.isTerminalProcessingStatus(processing.status))
      .map((processing: HypersceneProcessingView) => processing.id);
  }

  private isTerminalProcessingStatus(status: string | null | undefined): boolean {
    if (status === null || status === undefined) {
      return false;
    }

    return status === 'Completed' || status === 'Failed' || status === 'Canceled';
  }

  private makeSceneTreeNode(node: HypersceneNodeSmallView): HypersceneSceneTreeNode {
    return hsMakeSceneTreeNode(node);
  }

  private filterSceneContentRoots(nodes: HypersceneNodeSmallView[]): HypersceneNodeSmallView[] {
    return nodes.filter((node: HypersceneNodeSmallView) => !isAnnotationNode(node));
  }

  private isDecodeCanceledError(error: unknown): boolean {
    if (error instanceof Error && error.message === DECODE_CANCELED_ERROR_MESSAGE) {
      return true;
    }

    return error instanceof DOMException && error.name === 'AbortError';
  }

  private buildSceneMetadataPayload(
    fileName: string | null,
    modelId: string | null = null,
    sceneNameOverride: string | null = null
  ): unknown[] {
    const timestamp: Date = new Date();
    const normalizedFileName: string = fileName !== null && fileName.trim().length > 0 ? fileName.trim() : 'scene';
    const modelPrefix: string = modelId !== null && modelId.trim().length > 0 ? `model:${modelId}` : 'model:unknown';
    const sceneName: string =
      sceneNameOverride !== null && sceneNameOverride.trim().length > 0
        ? sceneNameOverride.trim()
        : `${normalizedFileName} | file:${normalizedFileName} | ${modelPrefix} | ${timestamp.toLocaleString()}`;
    const metadataPayload: Record<string, string>[] = [
      { sceneName },
      { fileName: normalizedFileName },
      { sourceFileName: normalizedFileName },
      { createdAt: timestamp.toISOString() },
    ];

    if (modelId !== null && modelId.trim().length > 0) {
      metadataPayload.push({ modelId: modelId.trim() });
    }

    const sourceTag: string = this.ingestTag.trim();
    if (sourceTag.length > 0) {
      metadataPayload.unshift({ source: sourceTag });
    }

    return metadataPayload;
  }

  private buildRenamedMetadataPayload(
    metadata: unknown[] | undefined,
    key: 'modelName' | 'sceneName',
    value: string
  ): unknown[] {
    const merged: Record<string, string> = this.extractMetadataDictionary(metadata);
    merged[key] = value;

    return Object.entries(merged).map(([entryKey, entryValue]: [string, string]) => ({
      [entryKey]: entryValue,
    }));
  }

  private extractMetadataDictionary(metadata: unknown[] | undefined): Record<string, string> {
    const result: Record<string, string> = {};
    if (!Array.isArray(metadata)) {
      return result;
    }

    for (const entry of metadata) {
      if (typeof entry !== 'object' || entry === null) {
        continue;
      }

      const entryRecord: { value?: unknown } = entry as { value?: unknown };
      const payload: unknown = entryRecord.value ?? entry;
      if (typeof payload !== 'object' || payload === null) {
        continue;
      }

      Object.entries(payload as Record<string, unknown>).forEach(([entryKey, entryValue]: [string, unknown]) => {
        if (typeof entryValue === 'string' && entryValue.trim().length > 0) {
          result[entryKey] = entryValue.trim();
        }
      });
    }

    return result;
  }

  private resolveSelectedModelFileName(modelId: string): string | null {
    const model: HypersceneModelView | undefined = this.modelsRegistry.find(
      (modelView: HypersceneModelView) => modelView.id === modelId
    );
    if (model === undefined) {
      return this.ingestState.fileName;
    }

    return this.tryExtractModelDisplayName(model.metadata) ?? this.ingestState.fileName;
  }

  private tryExtractMetadataString(metadata: unknown[] | undefined, key: string): string | null {
    if (!Array.isArray(metadata)) {
      return null;
    }

    for (const entry of metadata) {
      if (typeof entry !== 'object' || entry === null) {
        continue;
      }

      const entryRecord: { value?: unknown } = entry as { value?: unknown };
      const payload: unknown = entryRecord.value ?? entry;
      if (typeof payload !== 'object' || payload === null) {
        continue;
      }

      const value: unknown = (payload as Record<string, unknown>)[key];
      if (typeof value !== 'string') {
        continue;
      }

      const normalizedValue: string = value.trim();
      if (normalizedValue.length > 0) {
        return normalizedValue;
      }
    }

    return null;
  }

  private resolveModelCreatedAt(model: HypersceneModelView): string {
    const directCreatedAt: string | null = model.createdAt ?? model.uploadedAt ?? null;
    if (directCreatedAt !== null && directCreatedAt.trim().length > 0) {
      return this.formatModelDate(directCreatedAt);
    }

    const metadataCreatedAt: string | null = this.tryExtractModelCreatedAt(model.metadata);
    if (metadataCreatedAt !== null) {
      return this.formatModelDate(metadataCreatedAt);
    }

    const processingDates: string[] = this.processingsRegistry
      .filter((processing: HypersceneProcessingView) => processing.modelId === model.id)
      .flatMap((processing: HypersceneProcessingView) => [processing.completed, processing.started])
      .filter((value: string | null | undefined): value is string => value !== undefined && value !== null)
      .sort((left: string, right: string) => Date.parse(right) - Date.parse(left));

    return this.formatModelDate(processingDates[0] ?? null);
  }

  private tryExtractModelCreatedAt(metadata: unknown[] | undefined): string | null {
    for (const key of ['createdAt', 'uploadedAt', 'uploadDate']) {
      const value: string | null = this.tryExtractMetadataString(metadata, key);
      if (value !== null) {
        return value;
      }
    }

    return null;
  }

  private formatModelDate(value: string | null): string {
    if (value === null) {
      return '-';
    }

    const timestamp: number = Date.parse(value);
    if (Number.isNaN(timestamp)) {
      return value;
    }

    return `${new Date(timestamp).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  }

  private tryExtractModelFileName(metadata: unknown[] | undefined): string | null {
    return this.tryExtractMetadataString(metadata, 'fileName');
  }

  private tryExtractModelDisplayName(metadata: unknown[] | undefined): string | null {
    return this.tryExtractMetadataString(metadata, 'modelName') ?? this.tryExtractModelFileName(metadata);
  }

  private syncSelectedModelRenameDraft(): void {
    if (this.selectedModelId === null) {
      this.selectedModelRenameDraft = '';
      return;
    }

    const model: HypersceneModelView | undefined = this.modelsRegistry.find(
      (modelView: HypersceneModelView) => modelView.id === this.selectedModelId
    );
    if (model === undefined) {
      this.selectedModelRenameDraft = '';
      return;
    }

    this.selectedModelRenameDraft = this.tryExtractModelDisplayName(model.metadata) ?? '';
  }

  private syncSelectedSceneRenameDraft(): void {
    if (this.selectedSceneId === null) {
      this.selectedSceneRenameDraft = '';
      return;
    }

    const scene: HypersceneSceneView | undefined = this.scenesRegistry.find(
      (sceneView: HypersceneSceneView) => sceneView.id === this.selectedSceneId
    );
    if (scene === undefined) {
      this.selectedSceneRenameDraft = '';
      return;
    }

    this.selectedSceneRenameDraft = this.tryExtractMetadataString(scene.metadata, 'sceneName') ?? '';
  }

  private setIngestError(message: string): void {
    this.ingestState = {
      ...this.ingestState,
      phase: 'failed',
      error: message,
      message: 'Ingest pipeline failed.',
    };
    this.changeDetector.markForCheck();
  }

  private inferParserType(fileName: string): string {
    const dotIndex: number = fileName.lastIndexOf('.');
    if (dotIndex < 0 || dotIndex === fileName.length - 1) {
      return 'ifc';
    }

    return fileName.slice(dotIndex + 1).toLowerCase();
  }

  private async delay(ms: number): Promise<void> {
    await new Promise<void>((resolve: () => void) => {
      window.setTimeout(resolve, ms);
    });
  }
}
/* eslint-enable @typescript-eslint/member-ordering */
